import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Music4, DollarSign, Lock, Save, Sparkles, ScrollText, RefreshCw, Trash2,
  Play, Pause, X, SkipBack, SkipForward, Camera, LogOut, Search, Bell,
  Menu, ChevronDown, ChevronUp, MoreHorizontal, Plus, CalendarDays, ShoppingBag, Users,
} from "lucide-react";
import { api, setAdminToken } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import Dashboard from "@/components/admin/Dashboard";
import { Sidebar, MobileNav, MoreSheet } from "@/components/admin/Shell";
import { isTelegramOrder, orderSource, plural } from "@/components/admin/utils";

const C = { accent: "#F97316", gold: "#EAB308", neon: "#EC4899", green: "#10B981", card: "#131C2E" };
const PIE = ["#F97316", "#EAB308", "#EC4899", "#10B981", "#38BDF8", "#A78BFA", "#F43F5E"];

const GENRE = { pop: "Поп", rock: "Рок", rap: "Реп", chanson: "Шансон", disney: "Disney", guitar: "Гітара", dance: "Танц." };
const EVENT = { birthday: "🎂 ДН", jubilee: "🎉 Ювілей", wedding: "💍 Весілля", anniv: "❤️ Річниця", sorry: "🙏 Вибачення", prank: "😄 Розіграш", nooccasion: "✨ Без приводу", custom: "✍️ Своя" };
const STATUS = { completed: "text-[#10B981]", demo: "text-[#EAB308]", lyrics: "text-[#94A3B8]" };

const TAB_META = {
  dashboard: ["Панель", "Огляд роботи E-PISNYA за сьогодні"],
  payments: ["Замовлення", "Усі замовлення: статуси, оплати та генерації"],
  telegram: ["TG-замовлення", "Замовлення з Telegram-бота"],
  users: ["Користувачі", "База користувачів, баланс і доступ"],
  packages: ["Платежі", "Тарифи та ціни пакетів"],
  overview: ["Аналітика", "Динаміка пісень, жанри та виручка"],
  usage: ["Використання ШІ", "Виклики моделей, фоллбеки та витрати"],
  logs: ["Логи", "Системні події та помилки"],
  orders: ["Пісні", "Усі пісні та деталі замовлень"],
  generations: ["Генерації", "Останні згенеровані треки"],
  listen: ["Прослуховування", "Плеєр, обкладинки та посилання"],
  examples: ["Приклади звучання", "Приклади на головній сторінці застосунку"],
  links: ["Партнери", "Партнерські посилання та статистика"],
  fb: ["Facebook", "Кліки, ліди та покупки з реклами (метки g/utm)"],
  reviews: ["Підтримка", "Відгуки з сайту та модерація"],
  settings: ["Налаштування", "Провайдер музики та реквізити оплати"],
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("th_admin_token"));
  const [pin, setPin] = useState("");
  const [tab, setTab] = useState("dashboard");
  const [data, setData] = useState({ stats: null, users: [], usersMeta: null, songs: [], packages: [], settings: null, orders: [], usage: null });
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("today");
  const [uPage, setUPage] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [userModal, setUserModal] = useState(null);   // "fraud" | "banned" | null
  const [modalUsers, setModalUsers] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchFocus, setSearchFocus] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pageMenuOpen, setPageMenuOpen] = useState(false);
  const [boardSeed, setBoardSeed] = useState("");
  const [userSeed, setUserSeed] = useState("");
  const [pendingCreate, setPendingCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, so, p, st, or, us] = await Promise.all([
        api.get(`/admin/stats?period=${period}`),
        api.get(`/admin/users?page=${uPage}&limit=20`),
        api.get("/admin/songs"), api.get("/admin/packages"),
        api.get("/admin/settings"), api.get("/admin/orders"),
        api.get(`/admin/usage?period=${period}`),
      ]);
      setData({
        stats: s.data, users: u.data.items || [],
        usersMeta: { total: u.data.total, page: u.data.page, pages: u.data.pages },
        songs: so.data, packages: p.data, settings: st.data, orders: or.data || [],
        usage: us.data,
      });
    } catch (e) {
      if (e?.response?.status === 401) { setAdminToken(null); setAuthed(false); }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, [authed, period, uPage]);

  const openUserModal = async (kind) => {
    setUserModal(kind);
    setModalLoading(true);
    try {
      const r = await api.get(`/admin/users?kind=${kind}&limit=100`);
      setModalUsers(r.data.items || []);
    } catch {
      setModalUsers([]);
    } finally { setModalLoading(false); }
  };

  const banFromModal = async (tgId, banned) => {
    await toggleBan(tgId, banned);
    if (userModal) await openUserModal(userModal);
  };

  const toggleBan = async (tgId, banned) => {
    try {
      const r = await api.post(`/admin/users/${tgId}/ban`, { banned });
      toast.success(banned
        ? "Користувача заблоковано (отримає лише 🔒)"
        : `Розблоковано${r.data?.message_sent ? " · повідомлення надіслано" : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не вдалося змінити статус");
    }
  };

  const orderAction = async (oid, action) => {
    try {
      if (action === "refresh") {
        await load();
        return;
      }
      if (["unpaid", "pending", "paid", "done", "in_progress"].includes(action)) {
        await api.patch(`/admin/orders/${oid}/status`, { status: action });
        const labels = {
          unpaid: "Без оплати", pending: "На перевірці", paid: "Оплачено",
          in_progress: "На виконанні", done: "Виконано",
        };
        toast.success(`Статус: ${labels[action]}`);
        await load();
        return;
      }
      const r = await api.post(`/admin/orders/${oid}/${action}`);
      toast.success(action === "approve"
        ? `Зараховано ${r.data?.songs ?? ""} ген.`
        : "Замовлення відхилено");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не вдалося");
    }
  };

  const grant = async (tgId) => {
    const raw = window.prompt("Скільки генерацій нарахувати?", "1");
    if (!raw) return;
    const amount = parseInt(raw, 10);
    if (!amount || amount < 1) { toast.error("Некоректна кількість"); return; }
    try {
      const r = await api.post(`/admin/users/${tgId}/grant`, { amount });
      toast.success(`Нараховано +${amount}${r.data?.message_sent ? " · повідомлення надіслано в бот" : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не вдалося нарахувати");
    }
  };

  const login = async () => {
    try {
      const r = await api.post("/admin/login", { pin });
      setAdminToken(r.data.token);
      setAuthed(true);
      toast.success("Вхід виконано");
    } catch { toast.error("Невірний PIN"); }
  };

  const logout = () => { setAdminToken(null); setAuthed(false); };

  const go = (id) => {
    setTab(id);
    setSidebarOpen(false);
    setMoreOpen(false);
    setSearchFocus(false);
  };

  const openCreate = () => {
    setPendingCreate(true);
    go("payments");
  };

  const pickOrder = (o) => {
    setBoardSeed(String(o.number || ""));
    setSearch("");
    go("payments");
  };

  const pickUser = (u) => {
    setUserSeed(String(u.username || u.telegram_id || ""));
    setSearch("");
    go("users");
  };

  const pickSong = () => {
    setSearch("");
    go("orders");
  };

  const monthRange = useMemo(() => {
    const months = ["січ.", "лют.", "бер.", "квіт.", "трав.", "черв.", "лип.", "серп.", "вер.", "жовт.", "лист.", "груд."];
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return `${first.getDate()} ${months[first.getMonth()]} ${first.getFullYear()} — ${last.getDate()} ${months[last.getMonth()]} ${last.getFullYear()}`;
  }, []);

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return null;
    const ordersHit = (data.orders || []).filter((o) =>
      String(o.number || "").toLowerCase().includes(q)
      || String(o.landing_contact || "").toLowerCase().includes(q)
      || String(o.landing_description || "").toLowerCase().includes(q)
      || String(o.landing_event_label || "").toLowerCase().includes(q)
    ).slice(0, 4);
    const usersHit = (data.users || []).filter((u) =>
      String(u.telegram_id || "").includes(q)
      || String(u.username || "").toLowerCase().includes(q)
      || String(u.first_name || "").toLowerCase().includes(q)
    ).slice(0, 3);
    const songsHit = (data.songs || []).filter((s) =>
      String(s.event || "").toLowerCase().includes(q)
      || String(s.genre || "").toLowerCase().includes(q)
      || String(s.mood || "").toLowerCase().includes(q)
      || String(s.title || "").toLowerCase().includes(q)
    ).slice(0, 3);
    return { ordersHit, usersHit, songsHit, count: ordersHit.length + usersHit.length + songsHit.length };
  }, [search, data.orders, data.users, data.songs]);

  if (!authed) {
    return (
      <div className="adm-root min-h-screen grid place-items-center px-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at 28% 18%, rgba(53,110,255,0.16), transparent 62%), radial-gradient(circle at 76% 84%, rgba(124,77,255,0.14), transparent 62%)" }} />
        <div className="adm-card rounded-3xl p-8 w-full max-w-sm relative">
          <div className="grid place-items-center w-14 h-14 rounded-2xl adm-grad mx-auto mb-5"><Lock size={24} className="text-white" /></div>
          <h1 className="text-xl font-bold text-center text-white">Адмін-панель «E-PISNYA»</h1>
          <p className="text-sm text-[#94A3BD] text-center mt-1">Введіть PIN для доступу</p>
          <input
            data-testid="admin-pin-input"
            type="password" value={pin} onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="• • • •"
            className="adm-input mt-6 w-full px-4 py-3 text-center text-lg font-mono tracking-[0.4em]"
          />
          <button data-testid="admin-login-button" onClick={login}
            className="adm-grad-cta text-white font-bold w-full py-3 rounded-xl mt-4">Увійти</button>
          <Link to="/" className="block text-center text-xs text-[#64748B] mt-4 hover:text-white transition-colors">← На головну</Link>
        </div>
      </div>
    );
  }

  const activeOrders = (data.orders || []).filter((o) =>
    ["created", "screenshot_wait", "pending", "unpaid"].includes(o.status)).length;

  return (
    <div className="adm-root min-h-screen text-[#F5F7FF]">
      <Sidebar tab={tab} onNavigate={go} badges={{ payments: activeOrders }}
        className="hidden lg:flex fixed inset-y-0 left-0 w-[220px] z-50" />

      {sidebarOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[264px] shadow-2xl">
            <Sidebar tab={tab} onNavigate={go} badges={{ payments: activeOrders }} />
          </div>
        </div>
      )}

      <div className="lg:pl-[220px] flex flex-col min-h-screen">
        <header className="adm-topbar sticky top-0 z-40">
          <div className="h-16 px-4 sm:px-6 flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)}
              className="lg:hidden grid place-items-center w-10 h-10 rounded-xl border border-white/[0.07] text-[#94A3BD] hover:text-white transition-colors shrink-0">
              <Menu size={19} />
            </button>
            <div className="lg:hidden flex items-center gap-2 mr-1 shrink-0">
              <span className="grid place-items-center w-8 h-8 rounded-lg adm-grad"><Music4 size={15} className="text-white" /></span>
              <span className="font-display font-extrabold text-sm text-white tracking-wide">E-PISNYA</span>
            </div>

            <div className="hidden md:block relative w-[350px] max-w-[38vw] shrink-0">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none" />
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setSearchFocus(true); }}
                onFocus={() => setSearchFocus(true)}
                placeholder="Пошук (користувач, замовлення, пісня...)"
                className="adm-input w-full h-10 pl-9 pr-3 text-[13px]"
              />
              {searchFocus && searchResults && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setSearchFocus(false)} />
                  <div className="absolute left-0 right-0 top-12 z-50 adm-card rounded-2xl p-2 max-h-[70vh] overflow-y-auto adm-scroll">
                    {!searchResults.count && (
                      <div className="px-3 py-6 text-center text-xs text-[#64748B]">Нічого не знайдено</div>
                    )}
                    {!!searchResults.ordersHit.length && (
                      <>
                        <div className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-[#475569]">Замовлення</div>
                        {searchResults.ordersHit.map((o) => (
                          <button key={o._id} onClick={() => pickOrder(o)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-white/[0.04] transition-colors">
                            <ShoppingBag size={15} className="text-[#2498FF] shrink-0" />
                            <span className="min-w-0 flex-1">
                              <span className="block text-[13px] font-medium text-white truncate">
                                #{o.number || "—"} · {o.landing_contact || `TG ${o.user_telegram_id || "—"}`}
                              </span>
                              <span className="block text-[11px] text-[#64748B] truncate">{o.landing_description || o.package_name || "—"}</span>
                            </span>
                            <span className="text-[11px] font-semibold text-[#94A3BD] shrink-0">{(o.amount || 0) ? `${Math.round(o.amount)} ₴` : ""}</span>
                          </button>
                        ))}
                      </>
                    )}
                    {!!searchResults.usersHit.length && (
                      <>
                        <div className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-[#475569]">Користувачі</div>
                        {searchResults.usersHit.map((u) => (
                          <button key={u.telegram_id} onClick={() => pickUser(u)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-white/[0.04] transition-colors">
                            <Users size={15} className="text-[#7C4DFF] shrink-0" />
                            <span className="min-w-0 flex-1">
                              <span className="block text-[13px] font-medium text-white truncate">{u.username ? `@${u.username}` : (u.first_name || "—")}</span>
                              <span className="block text-[11px] text-[#64748B] font-mono">{u.telegram_id}</span>
                            </span>
                          </button>
                        ))}
                      </>
                    )}
                    {!!searchResults.songsHit.length && (
                      <>
                        <div className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-[#475569]">Пісні</div>
                        {searchResults.songsHit.map((s, i) => (
                          <button key={(s.id || s.created_at || "") + i} onClick={pickSong}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-white/[0.04] transition-colors">
                            <Music4 size={15} className="text-[#18C8FF] shrink-0" />
                            <span className="min-w-0 flex-1">
                              <span className="block text-[13px] font-medium text-white truncate">
                                {GENRE[s.genre] || s.genre || "Пісня"}{s.mood ? ` · ${s.mood}` : ""}
                              </span>
                              <span className="block text-[11px] text-[#64748B]">{s.status || ""}</span>
                            </span>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex-1" />

            <div className="hidden xl:flex items-center gap-2 text-xs text-[#94A3BD] px-3 h-10 rounded-xl border border-white/[0.07] bg-[#0B1729]/60 whitespace-nowrap">
              <CalendarDays size={14} className="text-[#2498FF]" />
              {monthRange}
            </div>
            <button data-testid="admin-refresh" title="Оновити дані"
              onClick={() => { setRefreshKey((k) => k + 1); load(); }}
              className="hidden sm:grid place-items-center w-10 h-10 rounded-xl border border-white/[0.07] text-[#94A3BD] hover:text-white hover:border-[#2498FF]/40 transition-colors shrink-0">
              <RefreshCw size={17} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={() => go("payments")} title="Активні замовлення"
              className="relative grid place-items-center w-10 h-10 rounded-xl border border-white/[0.07] text-[#94A3BD] hover:text-white hover:border-[#2498FF]/40 transition-colors shrink-0">
              <Bell size={18} />
              {activeOrders > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full adm-grad text-[10px] font-bold text-white grid place-items-center">{activeOrders}</span>
              )}
            </button>
            <button data-testid="admin-logout" onClick={logout} title="Вихід"
              className="hidden sm:grid place-items-center w-10 h-10 rounded-xl border border-white/[0.07] text-[#94A3BD] hover:text-[#FF8A8A] hover:border-[#FF5C7A]/40 transition-colors shrink-0">
              <LogOut size={17} />
            </button>
            <div className="relative shrink-0">
              <button onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2.5 px-1.5 sm:pr-2 h-11 rounded-xl hover:bg-white/[0.04] transition-colors">
                <span className="grid place-items-center w-8 h-8 rounded-full adm-grad text-xs font-bold text-white">Г</span>
                <span className="hidden sm:block text-left leading-tight">
                  <span className="block text-[13px] font-semibold text-white">Глеб</span>
                  <span className="block text-[11px] text-[#64748B]">Адміністратор</span>
                </span>
                <ChevronDown size={14} className="hidden sm:block text-[#64748B]" />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-12 z-50 w-48 adm-card rounded-2xl p-1.5">
                    <button onClick={() => { setMenuOpen(false); setRefreshKey((k) => k + 1); load(); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] text-[#CBD5E1] hover:bg-white/[0.05] transition-colors">
                      <RefreshCw size={15} /> Оновити дані
                    </button>
                    <Link to="/" onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] text-[#CBD5E1] hover:bg-white/[0.05] transition-colors">
                      <Music4 size={15} /> Лендінг
                    </Link>
                    <button onClick={logout}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] text-[#FF8A8A] hover:bg-[#FF5C7A]/10 transition-colors">
                      <LogOut size={15} /> Вихід
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 w-full max-w-[1600px] mx-auto px-4 sm:px-6 py-5 sm:py-6 pb-28 lg:pb-10">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
            <div>
              <h1 className="font-display text-2xl sm:text-[32px] font-bold leading-tight text-white">
                {tab === "dashboard" ? "Вітаємо, Глеб! 👋" : (TAB_META[tab]?.[0] || "Адмінка")}
              </h1>
              <p className="text-sm text-[#94A3BD] mt-1">
                {tab === "dashboard" ? "Огляд роботи E-PISNYA за сьогодні" : (TAB_META[tab]?.[1] || "")}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={openCreate}
                className="adm-grad-cta h-11 px-4 rounded-xl text-sm font-semibold text-white flex items-center gap-2">
                <Plus size={16} /> Створити пісню
              </button>
              <div className="relative">
                <button onClick={() => setPageMenuOpen((v) => !v)}
                  className="grid place-items-center w-11 h-11 rounded-xl adm-card text-[#94A3BD] hover:text-white transition-colors">
                  <MoreHorizontal size={18} />
                </button>
                {pageMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setPageMenuOpen(false)} />
                    <div className="absolute right-0 top-12 z-50 w-52 adm-card rounded-2xl p-1.5">
                      <button
                        onClick={() => { setPageMenuOpen(false); setRefreshKey((k) => k + 1); load(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] text-[#CBD5E1] hover:bg-white/[0.05] transition-colors">
                        <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Оновити дані
                      </button>
                      <Link to="/" onClick={() => setPageMenuOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] text-[#CBD5E1] hover:bg-white/[0.05] transition-colors">
                        <Music4 size={15} /> Лендінг
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          {tab === "dashboard" ? (
            <Dashboard
              stats={data.stats}
              orders={data.orders}
              users={data.users}
              usersTotal={data.usersMeta?.total ?? 0}
              songs={data.songs}
              packages={data.packages}
              loading={loading}
              period={period}
              setPeriod={setPeriod}
              onTab={go}
              onModerate={openUserModal}
            />
          ) : (
            <div>
              {tab === "overview" && <Overview stats={data.stats} />}
              {tab === "users" && <UsersTable users={data.users} meta={data.usersMeta}
                                            onPage={setUPage} onGrant={grant}
                                            onBan={toggleBan} seed={userSeed}
                                            onSeedHandled={() => setUserSeed("")} />}
              {tab === "payments" && <OrdersBoard orders={data.orders} onAction={orderAction}
                                            createPending={pendingCreate}
                                            onCreateHandled={() => setPendingCreate(false)}
                                            seedQuery={boardSeed}
                                            onSeedHandled={() => setBoardSeed("")} />}
              {tab === "telegram" && (
                <OrdersBoard orders={(data.orders || []).filter((o) => isTelegramOrder(o))}
                  onAction={orderAction} title="TG-замовлення" />
              )}
              {tab === "orders" && <OrdersTable songs={data.songs} />}
              {tab === "generations" && <GenerationsTable key={refreshKey} />}
              {tab === "listen" && <ListenTab key={refreshKey} />}
              {tab === "examples" && <ExamplesTab key={refreshKey} />}
              {tab === "links" && <LinksPanel key={refreshKey} />}
              {tab === "fb" && <FbPanel key={refreshKey} />}
              {tab === "reviews" && <ReviewsPanel key={refreshKey} />}
              {tab === "packages" && <Packages packages={data.packages} reload={load} />}
              {tab === "settings" && <SettingsPanel settings={data.settings} reload={load} />}
              {tab === "usage" && <UsagePanel usage={data.usage} />}
              {tab === "logs" && <LogsPanel key={refreshKey} />}
            </div>
          )}
        </main>
      </div>

      <MobileNav tab={tab} onNavigate={go} onMore={() => setMoreOpen(true)}
        moreActive={["telegram", "generations", "listen", "examples", "links", "reviews", "packages", "overview", "usage", "logs", "settings"].includes(tab)} />
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} tab={tab} onNavigate={go} />

      <Dialog open={!!userModal} onOpenChange={(o) => !o && setUserModal(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-2xl bg-[#0F1729] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>
              {userModal === "banned" ? "🔒 Забанені користувачі" : "🛡 Антифрод — халявщики"}
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              {userModal === "banned"
                ? "Ці користувачі отримують лише 🔒. Натисніть, щоб розбанити."
                : "Без покупок, але з багатьма безкоштовними текстами. Можна забанити одним кліком."}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-2">
            {modalLoading && <div className="text-[#64748B] text-sm py-6 text-center">Завантаження…</div>}
            {!modalLoading && !modalUsers.length && (
              <div className="text-[#475569] text-sm py-8 text-center">Тут порожньо ✓</div>
            )}
            {modalUsers.map((u) => (
              <div key={u.telegram_id}
                className="flex items-center justify-between gap-3 px-2 py-3 border-b border-white/5">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">
                    {u.username ? "@" + u.username : (u.first_name || "—")}
                    <span className="ml-2 font-mono text-xs text-[#64748B]">{u.telegram_id}</span>
                  </div>
                  <div className="text-xs text-[#64748B]">
                    безкоштовних текстів: {u.free_lyrics_used ?? 0} · витратив: {u.total_spent ?? 0}₴
                    · на балансі: {u.balance ?? 0}
                  </div>
                </div>
                {u.is_banned ? (
                  <button onClick={() => banFromModal(u.telegram_id, false)}
                    className="shrink-0 text-xs px-3 py-1.5 rounded-lg bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25">
                    ✅ Розбанити
                  </button>
                ) : (
                  <button onClick={() => banFromModal(u.telegram_id, true)}
                    className="shrink-0 text-xs px-3 py-1.5 rounded-lg bg-[#EF4444]/15 text-[#EF4444] hover:bg-[#EF4444]/25">
                    🚫 Забанити
                  </button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Panel = ({ title, children, className = "" }) => (
  <div className={`adm-card rounded-2xl p-5 ${className}`}>
    <h3 className="text-[15px] font-semibold text-white mb-4">{title}</h3>
    {children}
  </div>
);

function Overview({ stats }) {
  if (!stats) return <div className="text-[#64748B] p-8 text-center">Завантаження…</div>;
  const genres = (stats.genres || []).map((g) => ({ ...g, name: GENRE[g.name] || g.name }));
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <Panel title="Пісні по днях (14 днів)" className="lg:col-span-2">
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={stats.daily}>
            <defs>
              <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.accent} stopOpacity={0.5} />
                <stop offset="100%" stopColor={C.accent} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
            <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={{ background: C.card, border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
            <Area type="monotone" dataKey="songs" stroke={C.accent} strokeWidth={2} fill="url(#g1)" />
          </AreaChart>
        </ResponsiveContainer>
      </Panel>
      <Panel title="Топ жанрів">
        {genres.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={genres} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {genres.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: C.card, border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Panel>
      <Panel title="Виручка по днях" className="lg:col-span-3">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={stats.daily}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
            <YAxis stroke="#64748B" fontSize={11} />
            <Tooltip contentStyle={{ background: C.card, border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
            <Bar dataKey="revenue" fill={C.gold} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
}

const Empty = () => <div className="h-[220px] grid place-items-center text-[#64748B] text-sm"><div className="text-center"><Sparkles className="mx-auto mb-2 opacity-40" /> Ще немає даних</div></div>;

const Th = ({ children }) => <th className="text-left font-mono text-xs uppercase tracking-wider text-[#64748B] px-4 py-3">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 text-sm ${className}`}>{children}</td>;

// Ручной ввод расхода на рекламу по метке: своё состояние + явная кнопка «зберегти».
function SpendInput({ value, onSave }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  const save = () => {
    const n = Math.max(0, Number(String(v).replace(",", ".")) || 0);
    onSave(n);
  };
  return (
    <div className="flex items-center gap-1.5">
      <input type="number" min="0" step="10" value={v} placeholder="0"
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); }}
        className="w-20 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white outline-none focus:border-[#2BA8FF]" />
      <button onClick={save} title="Зберегти витрати"
        className="text-xs px-2 py-1.5 rounded-lg bg-[#2BA8FF]/20 text-[#8CCBFF] hover:bg-[#2BA8FF]/40 transition-colors">
        ✓
      </button>
    </div>
  );
}

function FbPanel() {
  const [data, setData] = useState({ items: [], total: {} });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("today");
  const [network, setNetwork] = useState("fb");
  const [onlyResult, setOnlyResult] = useState(true);
  const [sortKey, setSortKey] = useState("purchases");
  const [sortDir, setSortDir] = useState(-1);
  const load = async (p = period) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/fb?period=${p}&network=${network}`);
      setData(r.data || { items: [], total: {} });
    } catch (e) {
      if (e?.response?.status === 401) { setAdminToken(null); window.location.reload(); }
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line
  useEffect(() => { load(period); }, [period, network]);
  const t = data.total || {};
  const pt = data.partner_total || {};
  const totalPurchases = (r) => (r.purchases_bot ?? 0) + (r.purchases_site ?? 0)
    + (r.purchases_app ?? 0);
  const roiOf = (r) => ((r.spend || 0) > 0 ? (r.income || 0) / r.spend : null);
  const val = (r, k) => {
    if (k === "purchases") return totalPurchases(r);
    if (k === "roi") return roiOf(r) ?? -1;
    return r[k] ?? 0;
  };
  const items = (data.items || [])
    .filter((r) => !onlyResult || (r.leads ?? 0) > 0)
    .slice()
    .sort((a, b) => sortDir * (val(b, sortKey) - val(a, sortKey)));
  const sortBy = (k) => {
    if (sortKey === k) setSortDir((d) => -d);
    else { setSortKey(k); setSortDir(-1); }
  };
  const saveSpend = async (label, amount) => {
    const amt = Math.max(0, Number(amount) || 0);
    try {
      await api.post("/admin/fb/spend", { label, amount: amt, network });
      setData((prev) => {
        const its = (prev.items || []).map((x) => (x.label === label ? { ...x, spend: amt } : x));
        return { items: its, total: { ...(prev.total || {}),
          spend: its.reduce((s, x) => s + (x.spend || 0), 0) } };
      });
      toast.success("Витрати збережено");
    } catch { toast.error("Не вдалося зберегти витрати"); }
  };
  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["Метка", "Кліки", "Ліди бот", "Ліди сайт", "Покупці", "Покупки",
                  "бот", "сайт", "апка", "Дохід", "Витрати", "ROI"];
    const lines = [head.join(";")];
    items.forEach((r) => {
      const roi = roiOf(r);
      lines.push([esc(r.label), r.clicks, r.leads_bot ?? 0, r.leads_site ?? 0, r.buyers,
                  totalPurchases(r), r.purchases_bot ?? 0, r.purchases_site ?? 0,
                  r.purchases_app ?? 0, Math.round(r.income || 0), r.spend || 0,
                  roi === null ? "" : roi.toFixed(2)].join(";"));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${network === "tt" ? "tt" : "fb"}_${period}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 3000);
  };
  const SortTh = ({ k, children }) => (
    <th onClick={() => sortBy(k)}
      className="text-left font-mono text-xs uppercase tracking-wider px-4 py-3 cursor-pointer select-none whitespace-nowrap"
      style={{ color: sortKey === k ? "#8CCBFF" : "#64748B" }}>
      {children}{sortKey === k ? (sortDir === -1 ? " ▼" : " ▲") : ""}
    </th>
  );
  return (
    <div className="space-y-5" data-testid="admin-fb">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl font-extrabold text-white">
            {network === "tt" ? "TikTok-реклама" : "Facebook-реклама"}
          </h2>
          <p className="text-xs text-[#64748B] mt-0.5">
            Кліки → ліди → покупки за метками. Клік по заголовку — сортування.
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-white/10 overflow-hidden">
            {[["fb", "Facebook"], ["tt", "TikTok"]].map(([k, l]) => (
              <button key={k} onClick={() => setNetwork(k)}
                className={"text-xs px-3 py-2 transition-colors " + (network === k
                  ? "bg-[#2BA8FF]/25 text-white font-bold"
                  : "bg-white/5 text-[#CBD5E1] hover:text-white")}>
                {l}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg border border-white/10 overflow-hidden">
            {[["today", "Сьогодні"], ["yesterday", "Вчора"], ["week", "7 днів"],
              ["all", "Весь час"]].map(([k, l]) => (
              <button key={k} onClick={() => setPeriod(k)}
                className={"text-xs px-3 py-2 transition-colors " + (period === k
                  ? "bg-[#2BA8FF]/25 text-white font-bold"
                  : "bg-white/5 text-[#CBD5E1] hover:text-white")}>
                {l}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-[#CBD5E1] cursor-pointer select-none">
            <input type="checkbox" checked={onlyResult}
              onChange={(e) => setOnlyResult(e.target.checked)} />
            Лише результативні
          </label>
          <button onClick={exportCsv}
            className="text-xs px-3 py-2 rounded-lg bg-white/5 text-[#CBD5E1] border border-white/10 hover:border-white/30 transition-colors">
            ⬇ CSV
          </button>
          <button onClick={() => load(period)}
            className="text-xs px-3 py-2 rounded-lg bg-white/5 text-[#CBD5E1] border border-white/10 hover:border-white/30 transition-colors">
            ↻ Оновити
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
        {[["Кліки", t.clicks ?? 0], ["Ліди (бот)", t.leads_bot ?? 0],
          ["Ліди (сайт)", t.leads_site ?? 0], ["Покупці", t.buyers ?? 0],
          ["Покупки (бот)", t.purchases_bot ?? 0], ["Покупки (сайт)", t.purchases_site ?? 0],
          ["Покупки (апка)", t.purchases_app ?? 0],
          ["Дохід", `${Math.round(t.income ?? 0)} ₴`],
          ["Витрати", `${Math.round(t.spend ?? 0)} ₴`],
          ["ROI", (t.spend ?? 0) > 0
            ? `×${((t.income ?? 0) / t.spend).toFixed(2)}` : "—"],
          ["Всього доходу", `${Math.round(data.income_total ?? 0)} ₴`],
          ["Партнерка", `${Math.round(pt.income ?? 0)} ₴`],
          ["Без атрибуції", `${Math.round(data.income_unattributed ?? 0)} ₴`]].map(([l, v]) => (
          <div key={l} className="adm-card rounded-2xl px-4 py-3">
            <div className="text-[11px] text-[#94A3BD]">{l}</div>
            <div className="text-xl font-bold text-white mt-1">{loading ? "…" : v}</div>
          </div>
        ))}
      </div>
      <div className="card-soft rounded-2xl overflow-hidden">
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full min-w-[920px]">
            <thead className="border-b border-white/10">
              <tr>
                <SortTh k="label">Метка</SortTh>
                <SortTh k="clicks">Кліки</SortTh>
                <SortTh k="leads_bot">Ліди (бот)</SortTh>
                <SortTh k="leads_site">Ліди (сайт)</SortTh>
                <SortTh k="buyers">Покупці</SortTh>
                <SortTh k="purchases">Покупки</SortTh>
                <SortTh k="purchases_bot">· бот</SortTh>
                <SortTh k="purchases_site">· сайт</SortTh>
                <SortTh k="purchases_app">· апка</SortTh>
                <SortTh k="income">Дохід</SortTh>
                <SortTh k="spend">Витрати ₴</SortTh>
                <SortTh k="roi">ROI</SortTh>
              </tr>
            </thead>
            <tbody>
              {items.length ? items.map((r) => {
                const tot = totalPurchases(r);
                const roi = roiOf(r);
                return (
                  <tr key={r.label}
                    className={"border-b border-white/5 " + (tot > 0
                      ? "bg-[#10B981]/[0.07]"
                      : "hover:bg-white/[0.02]")}>
                    <Td className="font-semibold text-white">{r.label}</Td>
                    <Td>{r.clicks}</Td>
                    <Td>{r.leads_bot ?? 0}</Td>
                    <Td className="text-[#8CCBFF]">{r.leads_site ?? 0}</Td>
                    <Td>{r.buyers}</Td>
                    <Td className={tot > 0 ? "font-bold text-[#6EE7B7]" : ""}>{tot}</Td>
                    <Td className="text-[#6EE7B7]">{r.purchases_bot ?? 0}</Td>
                    <Td className="text-[#6EE7B7]">{r.purchases_site ?? 0}</Td>
                    <Td className="text-[#6EE7B7]">{r.purchases_app ?? 0}</Td>
                    <Td className="text-[#EAB308]">{Math.round(r.income)} ₴</Td>
                    <Td>
                      <SpendInput value={r.spend}
                        onSave={(amount) => saveSpend(r.label, amount)} />
                    </Td>
                    <Td>
                      {roi === null ? <span className="text-[#64748B]">—</span> : (
                        <div>
                          <div className={"font-bold " + (roi >= 1 ? "text-[#6EE7B7]" : "text-[#F87171]")}>
                            ×{roi.toFixed(2)}
                          </div>
                          <div className="text-[11px] text-[#64748B]">
                            {(r.income - (r.spend || 0)) >= 0 ? "+" : ""}
                            {Math.round(r.income - (r.spend || 0))} ₴
                          </div>
                        </div>
                      )}
                    </Td>
                  </tr>
                );
              }              ) : (
                <tr><Td className="text-[#64748B]">{loading ? "Завантаження…"
                  : (onlyResult ? "Немає результативних меток за цей період"
                                : "Ще немає даних")}</Td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card-soft rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex flex-wrap items-center justify-between gap-2">
          <div className="font-display font-extrabold text-sm text-white">
            Партнерські посилання
          </div>
          <div className="text-xs text-[#64748B]">
            Всього: {pt.clicks ?? 0} кліків · {pt.purchases ?? 0} покупок · {Math.round(pt.income ?? 0)} ₴ ·
            {" "}виплата {Math.round(pt.reward ?? 0)} ₴
          </div>
        </div>
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full min-w-[720px]">
            <thead className="border-b border-white/10">
              <tr>
                <Th>Назва / код</Th><Th>Статус</Th><Th>Кліки</Th><Th>Ліди бот</Th><Th>Ліди сайт</Th>
                <Th>Покупці</Th><Th>Покупки</Th><Th>Дохід</Th><Th>Виплата 50%</Th>
              </tr>
            </thead>
            <tbody>
              {(data.partner_items || []).length ? data.partner_items.map((l) => (
                <tr key={l.code}
                  className={"border-b border-white/5 " + (l.purchases > 0
                    ? "bg-[#10B981]/[0.07]" : "hover:bg-white/[0.02]")}>
                  <Td className="font-semibold text-white">
                    {l.label}
                    <span className="ml-2 font-mono text-[11px] text-[#64748B]">{l.code}</span>
                  </Td>
                  <Td>
                    <span className={"text-xs px-2 py-0.5 rounded-full " + (l.active
                      ? "bg-[#10B981]/15 text-[#6EE7B7]" : "bg-white/10 text-[#94A3BD]")}>
                      {l.active ? "активна" : "вимкнена"}
                    </span>
                  </Td>
                  <Td>{l.clicks ?? 0}</Td>
                  <Td>{l.starts}</Td>
                  <Td>{l.site_leads ?? 0}</Td>
                  <Td>{l.buyers}</Td>
                  <Td className={l.purchases > 0 ? "font-bold text-[#6EE7B7]" : ""}>{l.purchases}</Td>
                  <Td className="text-[#EAB308]">{Math.round(l.income)} ₴</Td>
                  <Td className="text-[#8CCBFF]">{Math.round(l.reward)} ₴</Td>
                </tr>
              )) : (
                <tr><Td className="text-[#64748B]">{loading ? "Завантаження…"
                  : "Немає партнерських посилань"}</Td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function LinksPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState({ starts: 0, buyers: 0, purchases: 0, income: 0 });
  const [label, setLabel] = useState("");
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/links");
      setItems(r.data.items || []);
      setTotal(r.data.total || {});
    } catch (e) {
      if (e?.response?.status === 401) { setAdminToken(null); window.location.reload(); }
    }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/links",
        { label: label || "без назви", slug: slug || null });
      setFresh(r.data);
      setLabel(""); setSlug("");
      await load();
      toast.success("Посилання створено");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Помилка створення");
    } finally { setBusy(false); }
  };

  const toggle = async (it) => {
    try {
      await api.patch(`/admin/links/${it.code}`, { active: !it.active });
      await load();
    } catch { toast.error("Не вдалося змінити"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Видалити посилання «${it.label}»? Статистика по ньому зникне.`)) return;
    try {
      await api.delete(`/admin/links/${it.code}`);
      await load();
      toast.success("Видалено");
    } catch { toast.error("Не вдалося видалити"); }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Скопійовано ✓");
    } catch { toast.error("Скопіюйте вручну: " + url); }
  };

  return (
    <div className="space-y-5" data-testid="admin-links">
      <div className="card-soft rounded-2xl p-4">
        <div className="text-sm font-bold text-white mb-3">Нова посилання-джерело</div>
        <div className="flex flex-wrap gap-2">
          <input value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="Метка (напр. TikTok — ролик 1)"
            className="flex-1 min-w-[200px] px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none focus:border-white/30" />
          <input value={slug} onChange={(e) => setSlug(e.target.value)}
            placeholder="slug (необов'язково, латиниця)"
            className="w-56 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none focus:border-white/30" />
          <button onClick={create} disabled={busy}
            className="btn-tg grad-tg text-white font-bold text-sm px-5 py-2 rounded-lg disabled:opacity-50">
            {busy ? "…" : "Створити"}
          </button>
        </div>
        {fresh && (
          <div className="mt-3 text-sm text-[#94A3B8] space-y-1.5">
            <div>
              🤖 Бот: <span className="font-mono text-white">{fresh.url}</span>
              <button onClick={() => copy(fresh.url)}
                className="ml-2 px-2 py-0.5 rounded border border-white/10 hover:bg-white/5">📋</button>
            </div>
            {fresh.site_url && (
              <div>
                🌐 Сайт: <span className="font-mono text-white">{fresh.site_url}</span>
                <button onClick={() => copy(fresh.site_url)}
                  className="ml-2 px-2 py-0.5 rounded border border-white/10 hover:bg-white/5">📋</button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[["Лід (бот)", total.starts ?? 0], ["Лід (сайт)", total.site_leads ?? 0],
          ["Покупці", total.buyers ?? 0], ["Покупки", total.purchases ?? 0],
          ["Дохід, ₴", total.income ?? 0]].map(([l, v]) => (
          <div key={l} className="card-soft rounded-2xl p-4">
            <div className="text-xs text-[#94A3B8]">{l}</div>
            <div className="text-2xl font-black text-white mt-1">{v}</div>
          </div>
        ))}
      </div>

      <div className="card-soft rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[#64748B] text-xs text-left border-b border-white/10">
              <th className="px-4 py-3">Метка</th>
              <th className="px-4 py-3">Посилання</th>
              <th className="px-4 py-3">Переходів</th>
              <th className="px-4 py-3">Покупців</th>
              <th className="px-4 py-3">Покупок</th>
              <th className="px-4 py-3">Дохід</th>
              <th className="px-4 py-3">50%</th>
              <th className="px-4 py-3">Конверсія</th>
              <th className="px-4 py-3">Статус</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.code} className="border-t border-white/5 text-[#CBD5E1]">
                <td className="px-4 py-3 font-medium text-white">{it.label}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-[#94A3B8]">sl_{it.code}</span>
                    <button onClick={() => copy(it.url)} title={it.url}
                      className="px-2 py-0.5 rounded border border-white/10 hover:bg-white/5 text-xs">🤖 Бот</button>
                    <button onClick={() => copy(it.site_url)} title={it.site_url}
                      className="px-2 py-0.5 rounded border border-white/10 hover:bg-white/5 text-xs">🌐 Сайт</button>
                  </div>
                </td>
                <td className="px-4 py-3">{it.starts}</td>
                <td className="px-4 py-3">{it.buyers}</td>
                <td className="px-4 py-3">{it.purchases}</td>
                <td className="px-4 py-3 text-[#10B981]">{it.income}₴</td>
                <td className="px-4 py-3 text-[#94A3B8]">{it.reward}₴</td>
                <td className="px-4 py-3">{it.starts ? Math.round((it.buyers / it.starts) * 100) : 0}%</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggle(it)}
                    className={`text-xs px-2 py-1 rounded-full ${it.active
                      ? "bg-[#10B981]/15 text-[#10B981]" : "bg-white/5 text-[#64748B]"}`}>
                    {it.active ? "увімкнено" : "вимкнено"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(it)}
                    className="text-[#EF4444] hover:text-[#F87171]" title="Видалити">
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-[#475569]">
                Ще немає посилань — створіть перше вище
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-[#475569]">
        «Переходів» = людей прийшло (унікальні, при першому /start). Статистика оновлюється при відкритті вкладки.
      </div>
    </div>
  );
}

function GenerationsTable() {
  const [state, setState] = useState({ items: [], page: 0, pages: 1, total: 0, loading: true });
  const load = async (page = 0) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get(`/admin/generations?page=${page}&limit=20`);
      setState({ ...r.data, loading: false });
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      if (e?.response?.status === 401) { setAdminToken(null); window.location.reload(); }
    }
  };
  // eslint-disable-next-line
  useEffect(() => { load(0); }, []);
  const { items, page, pages, total, loading } = state;
  return (
    <div className="space-y-4" data-testid="admin-generations">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-[#94A3B8]">
          Всього треків: <b className="text-white">{total}</b> (по 20 на стор.)
        </div>
        <div className="flex items-center gap-2 text-sm">
          <button disabled={page <= 0 || loading} onClick={() => load(page - 1)}
            className="px-3 py-1.5 rounded-lg border border-white/10 text-[#CBD5E1] disabled:opacity-40 hover:bg-white/5">
            ← Назад
          </button>
          <span className="text-[#94A3B8]">{page + 1} / {pages}</span>
          <button disabled={page >= pages - 1 || loading} onClick={() => load(page + 1)}
            className="px-3 py-1.5 rounded-lg border border-white/10 text-[#CBD5E1] disabled:opacity-40 hover:bg-white/5">
            Вперед →
          </button>
        </div>
      </div>

      {loading && <div className="text-[#64748B] text-sm py-4">Завантаження…</div>}

      <div className="space-y-3">
        {items.map((g) => (
          <div key={g.id} className="card-soft rounded-2xl p-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-bold text-white">«{g.title || "—"}»</span>
              <span className="text-[#94A3B8] text-xs">
                {new Date(g.created_at).toLocaleString("uk-UA")}
              </span>
              <span className="font-mono text-xs text-[#64748B]">
                {g.username ? "@" + g.username : (g.first_name || "—")} · {g.tg_id}
              </span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-[#94A3B8]">
                {EVENT[g.event] || g.event || "—"} · {GENRE[g.genre] || g.genre || "—"}
              </span>
              <span className={`text-xs ${STATUS[g.status] || "text-[#94A3B8]"}`}>{g.status}</span>
            </div>
            {g.tracks?.length ? (
              <div className="mt-3 grid gap-2 lg:grid-cols-2">
                {g.tracks.map((url, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-[11px] text-[#64748B] w-24 shrink-0 truncate"
                          title={g.take_titles?.[i] || ""}>
                      {g.tracks.length > 1 ? `Варіант ${i + 1}` : "Трек"}
                      {g.take_titles?.[i] ? ` · ${g.take_titles[i]}` : ""}
                    </span>
                    <audio controls preload="none" src={url} className="h-8 w-full min-w-0" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-2 text-xs text-[#475569]">аудіо немає (у процесі або видалено)</div>
            )}
          </div>
        ))}
        {!loading && !items.length && (
          <div className="text-center py-10 text-[#475569] text-sm">Поки немає згенерованих треків</div>
        )}
      </div>
    </div>
  );
}

const PLAYER = {
  bg: "#050A14", surface: "#0B1426", blue: "#16B8FF",
  action: "#1677FF", yellow: "#FFD83D",
};

function fmtTime(s) {
  if (!isFinite(s) || s < 0) return "0:00";
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}

function WaveBars({ className = "", active = false, count = 15, scale = 1 }) {
  const hs = [10, 22, 34, 16, 42, 26, 12, 30, 20, 38, 14, 24, 32, 18, 28];
  return (
    <div className={`flex items-end justify-center gap-1 ${className}`}>
      {hs.slice(0, count).map((h, i) => (
        <span key={i} className={`w-1 rounded-full ${active ? "ep-wave" : ""}`}
          style={{
            height: Math.round(h * scale), opacity: 0.85, animationDelay: `${i * 80}ms`,
            background: i % 4 === 3 ? PLAYER.yellow : PLAYER.blue,
          }} />
      ))}
    </div>
  );
}

function CoverBox({ url, className = "", iconSize = 40 }) {
  if (url) return <img src={url} alt="" className={`object-cover ${className}`} />;
  return (
    <div className={`relative overflow-hidden ${className}`}
      style={{ background: "linear-gradient(135deg,#0B1426 0%,#12283F 55%,#0B1426 100%)" }}>
      <WaveBars className="absolute inset-x-0 bottom-5 opacity-50" />
      <div className="absolute inset-0 grid place-items-center text-[#1E3A5F]">
        <Music4 size={iconSize} />
      </div>
    </div>
  );
}

function squareJpeg(file, size = 900) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const side = Math.min(img.width, img.height);
        const c = document.createElement("canvas");
        c.width = c.height = size;
        c.getContext("2d").drawImage(
          img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, size, size);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function VariantPicker({ item, onPick, onClose }) {
  const tracks = item.tracks || [];
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center px-6"
      style={{ background: "rgba(5,10,20,.72)" }} onClick={onClose}>
      <div className="w-full max-w-[340px] rounded-3xl border border-white/10 p-5"
        style={{ background: PLAYER.surface }} onClick={(e) => e.stopPropagation()}>
        <div className="text-center font-bold text-white truncate"
          style={{ fontFamily: "Manrope, Inter, sans-serif" }}>
          {item.take_titles?.[0] || item.title || "Без назви"}
        </div>
        <div className="mt-1 text-center text-xs text-[#64748B]">Оберіть варіант для прослуховування</div>
        <div className="mt-4 space-y-2">
          {tracks.map((_, i) => (
            <button key={i} onClick={() => onPick(i)}
              className="w-full py-2.5 rounded-xl font-semibold text-sm truncate"
              style={{ background: "rgba(22,184,255,.12)", color: PLAYER.blue,
                       border: "1px solid rgba(22,184,255,.35)" }}>
              Варіант {i + 1}{item.take_titles?.[i] ? ` · ${item.take_titles[i]}` : ""}
            </button>
          ))}
        </div>
        <button onClick={onClose}
          className="mt-3 w-full py-2 rounded-xl text-sm text-[#94A3B8] hover:bg-white/5 transition-colors">
          Скасувати
        </button>
      </div>
    </div>
  );
}

const OWNER_ID = 5960268295;

function ListenTab() {
  const [state, setState] = useState({ items: [], page: 0, pages: 1, total: 0, loading: true });
  const [open, setOpen] = useState(null);     // { idx, take }
  const [pick, setPick] = useState(null);     // idx карточки, ждём выбор варианта
  const [mine, setMine] = useState(false);    // сортировка: только треки владельца
  const [share, setShare] = useState(null);   // {title, url}

  const shareSong = async (g) => {
    try {
      const r = await api.post(`/admin/songs/${g.id}/share`);
      setShare({ title: g.take_titles?.[0] || g.title || "Пісня", url: r.data.url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не вдалося створити посилання");
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(share?.url || "");
      toast.success("Скопійовано ✓");
    } catch { toast.error("Не вдалося скопіювати"); }
  };

  const load = async (page = 0, mineFlag = mine) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get(
        `/admin/generations?page=${page}&limit=10${mineFlag ? `&tg_id=${OWNER_ID}` : ""}`);
      setState({ ...r.data, loading: false });
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      if (e?.response?.status === 401) { setAdminToken(null); window.location.reload(); }
    }
  };
  const toggleMine = () => { const next = !mine; setMine(next); load(0, next); };
  // eslint-disable-next-line
  useEffect(() => { load(0); }, []);

  const patchItem = (id, patch) => setState((s) => ({
    ...s, items: s.items.map((it) => (it.id === id ? { ...it, ...patch } : it)),
  }));

  const { items, page, pages, total, loading } = state;
  const current = open != null ? items[open.idx] : null;
  const pickItem = pick != null ? items[pick] : null;

  return (
    <div className="space-y-5" data-testid="admin-listen">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm text-[#94A3B8]">
            Останні треки: <b className="text-white">{items.length}</b> з{" "}
            <b className="text-white">{total}</b> (по 10 на стор.)
          </div>
          <button onClick={toggleMine} data-testid="listen-sort"
            className="text-xs font-semibold rounded-xl px-3 py-1.5 border transition-colors"
            style={mine
              ? { background: "rgba(22,184,255,.15)", color: PLAYER.blue, borderColor: "rgba(22,184,255,.4)" }
              : { color: "#94A3B8", borderColor: "rgba(255,255,255,.12)" }}>
            🔀 Сортування{mine ? ` · мої (${OWNER_ID})` : ""}
          </button>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <button disabled={page <= 0 || loading} onClick={() => load(page - 1)}
            className="px-3 py-1.5 rounded-lg border border-white/10 text-[#CBD5E1] disabled:opacity-40 hover:bg-white/5">
            ← Назад
          </button>
          <span className="text-[#94A3B8]">{page + 1} / {pages}</span>
          <button disabled={page >= pages - 1 || loading} onClick={() => load(page + 1)}
            className="px-3 py-1.5 rounded-lg border border-white/10 text-[#CBD5E1] disabled:opacity-40 hover:bg-white/5">
            Вперед →
          </button>
        </div>
      </div>

      {loading && <div className="text-[#64748B] text-sm py-4">Завантаження…</div>}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((g, i) => (
          <div key={g.id} role="button" tabIndex={0}
            onClick={() => (g.tracks?.length > 1 ? setPick(i) : setOpen({ idx: i, take: 0 }))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (g.tracks?.length > 1) setPick(i); else setOpen({ idx: i, take: 0 });
              }
            }}
            className="group text-left rounded-2xl border border-white/10 overflow-hidden transition-transform hover:-translate-y-0.5 cursor-pointer"
            style={{ background: PLAYER.surface }}>
            <div className="relative aspect-square">
              <CoverBox url={g.cover_url} className="w-full h-full" />
              <button data-testid={`listen-share-${g.id}`}
                onClick={(e) => { e.stopPropagation(); shareSong(g); }}
                title="Отримати посилання на цю пісню"
                className="absolute top-2 right-2 z-10 grid place-items-center w-9 h-9 rounded-full border border-white/20 bg-black/50 backdrop-blur hover:border-[#2BA8FF]/60 text-white transition-colors">
                🔗
              </button>
              <span className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: "rgba(5,10,20,.45)" }}>
                <span className="w-14 h-14 rounded-full grid place-items-center"
                  style={{ background: PLAYER.action, boxShadow: "0 0 40px rgba(22,184,255,.5)" }}>
                  <Play size={22} className="text-white ml-0.5" fill="white" />
                </span>
              </span>
            </div>
            <div className="p-3.5 space-y-1.5">
              <div className="font-bold text-white truncate"
                style={{ fontFamily: "Manrope, Inter, sans-serif" }}>
                {g.take_titles?.[0] || g.title || "Без назви"}
              </div>
              <div className="text-xs text-[#64748B] truncate">
                {g.username ? "@" + g.username : (g.first_name || "—")} ·{" "}
                {new Date(g.created_at).toLocaleString("uk-UA")}
              </div>
              <div className="flex items-center gap-1.5 text-[11px]">
                <span className="px-2 py-0.5 rounded-full bg-white/5 text-[#94A3B8]">
                  {EVENT[g.event] || g.event || "—"}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-white/5 text-[#94A3B8]">
                  {GENRE[g.genre] || g.genre || "—"}
                </span>
                {g.tracks?.length > 1 && (
                  <span className="px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(22,184,255,.12)", color: PLAYER.blue }}>
                    {g.tracks.length} варіанти
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {!loading && !items.length && (
        <div className="text-center py-10 text-[#475569] text-sm">
          {mine ? "У цього користувача немає згенерованих треків" : "Поки немає згенерованих треків"}
        </div>
      )}

      {pickItem && (
        <VariantPicker item={pickItem}
          onPick={(t) => { setOpen({ idx: pick, take: t }); setPick(null); }}
          onClose={() => setPick(null)} />
      )}
      {current && (
        <PlayerOverlay item={current} initialTake={open.take}
          onClose={() => setOpen(null)}
          onChanged={(patch) => patchItem(current.id, patch)} />
      )}

      <Dialog open={!!share} onOpenChange={(o) => !o && setShare(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-md bg-[#0F1729] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-base truncate">
              🔗 Посилання на «{share?.title}»
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <code className="block w-full truncate rounded-xl bg-black/30 border border-white/10 px-3 py-2.5 text-sm text-[#8CCBFF]">
              {share?.url}
            </code>
            <div className="flex flex-wrap gap-2">
              <button onClick={copyShare}
                className="text-xs px-3 py-2 rounded-lg bg-[#2BA8FF]/10 text-[#7CC5FF] border border-[#2BA8FF]/30 hover:bg-[#2BA8FF]/20">
                📋 Копіювати
              </button>
              <a href={`https://t.me/share/url?url=${encodeURIComponent(share?.url || "")}&text=${encodeURIComponent(share?.title || "")}`}
                target="_blank" rel="noreferrer"
                className="text-xs px-3 py-2 rounded-lg border border-white/10 text-[#CBD5E1] hover:border-white/30">
                Telegram
              </a>
              <a href={`https://wa.me/?text=${encodeURIComponent(`${share?.title || ""} — ${share?.url || ""}`)}`}
                target="_blank" rel="noreferrer"
                className="text-xs px-3 py-2 rounded-lg border border-white/10 text-[#CBD5E1] hover:border-white/30">
                WhatsApp
              </a>
              <a href={`viber://forward?text=${encodeURIComponent(`${share?.title || ""} — ${share?.url || ""}`)}`}
                className="text-xs px-3 py-2 rounded-lg border border-white/10 text-[#CBD5E1] hover:border-white/30">
                Viber
              </a>
            </div>
            <p className="text-xs text-[#64748B]">
              Сторінка-подарунок: тільки ця пісня, з плеєром, скачуванням і пересиланням — без номера замовлення.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EX_GENRE_COLORS = {
  guitar: "#38BDF8", pop: "#FACC15", rnb: "#EC4899", dance: "#F43F5E",
  zastilna: "#6366F1", lightrock: "#8B5CF6", rock: "#8B5CF6", rap: "#10B981",
  deephouse: "#F43F5E", disney: "#EC4899", custom: "#38A8FF",
};

function ExamplesTab() {
  const [examples, setExamples] = useState({ items: [], total: 0, loading: true });
  const [meta, setMeta] = useState({ genres: [], moods: [] });
  const [mode, setMode] = useState("song");

  const [q, setQ] = useState("");
  const [songs, setSongs] = useState({ items: [], page: 0, hasMore: false, loading: true });

  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const [modal, setModal] = useState(null); // {kind, sel?, it?, real?, form:{title,genres,mood,color,sort}}
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [removingId, setRemovingId] = useState("");

  const authFail = (e) => {
    if (e?.response?.status === 401) { setAdminToken(null); window.location.reload(); return true; }
    return false;
  };

  const load = async () => {
    setExamples((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get("/admin/examples");
      const items = r.data?.items || [];
      setExamples({ items, total: r.data?.total ?? items.length, loading: false });
    } catch (e) {
      setExamples((s) => ({ ...s, loading: false }));
      if (!authFail(e)) toast.error(e?.response?.data?.detail || "Не вдалося завантажити приклади");
    }
  };

  const loadMeta = async () => {
    try {
      const r = await api.get("/admin/examples/meta");
      setMeta({ genres: r.data?.genres || [], moods: r.data?.moods || [] });
    } catch (e) { authFail(e); }
  };

  const loadSongs = async (page = 0, query = "") => {
    setSongs((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get(`/admin/examples/songs?q=${encodeURIComponent(query.trim())}&page=${page}&limit=20`);
      setSongs({ items: r.data?.items || [], page: r.data?.page ?? page, hasMore: !!r.data?.has_more, loading: false });
    } catch (e) {
      setSongs((s) => ({ ...s, loading: false }));
      if (!authFail(e)) toast.error(e?.response?.data?.detail || "Не вдалося завантажити пісні");
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); loadMeta(); }, []);

  // eslint-disable-next-line
  useEffect(() => {
    const t = setTimeout(() => loadSongs(0, q), q.trim() ? 400 : 0);
    return () => clearTimeout(t);
  }, [q]);

  const genreLabel = (key) => GENRE[key] || meta.genres.find((g) => g.key === key)?.label || key || "—";
  const genreColor = (key) => EX_GENRE_COLORS[key] || "#38A8FF";
  const modalGenreLabels = (form) => (form.genres || [])
    .map((g) => meta.genres.find((x) => x.key === g)?.label || g).join(" + ");

  const truncName = (s) => {
    const a = Array.from(String(s || "").trim());
    return a.length > 10 ? a.slice(0, 10).join("") + "…" : a.join("");
  };

  const refOf = (s) => {
    const base = String(s.id || "").startsWith("o:") ? `Замовлення #${String(s.id).split(":")[1] || "—"}` : "";
    const who = s.username ? `@${s.username}` : (s.tg_id ? `TG ${s.tg_id}` : "");
    return [base, who].filter(Boolean).join(" · ");
  };

  const validGenres = (list) => (list || [])
    .filter((g) => g && g !== "mix" && meta.genres.some((m) => m.key === g)).slice(0, 3);

  const closeModal = () => { if (!busy) setModal(null); };

  const pickVariant = (s, i) => {
    const gs = validGenres(s.genres && s.genres.length ? s.genres : [s.genre]);
    const moodOk = meta.moods.some((m) => m.key === s.mood) ? s.mood : "";
    const title = String(s.take_titles?.[i] || s.title || "").slice(0, 80);
    setModal({
      kind: "song", sel: { item: s, take: i },
      real: { genres: gs, mood: moodOk },
      form: { title, show_title: false, genres: gs, mood: moodOk, color: "", sort: 0 },
    });
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Файл завеликий (макс 25 МБ)"); return; }
    setModal({
      kind: "upload",
      form: { title: String(f.name.replace(/\.[^.]+$/, "") || "").slice(0, 80), show_title: false, genres: [], mood: "", color: "", sort: 0 },
    });
  };

  const resumeUpload = () => {
    if (!file) { fileRef.current?.click(); return; }
    pickFile({ target: { files: [file] } });
  };

  const startEdit = (it) => {
    const gs = validGenres(it.genres && it.genres.length ? it.genres : [it.genre]);
    setModal({
      kind: "edit", it,
      form: {
        title: it.title || "", show_title: !!it.show_title,
        genres: gs, mood: it.mood || "",
        color: it.color || "", sort: Number(it.sort) || 0,
      },
    });
  };

  const toggleGenre = (key) => setModal((m) => {
    if (!m) return m;
    const has = m.form.genres.includes(key);
    const gs = has ? m.form.genres.filter((g) => g !== key) : [...m.form.genres, key].slice(0, 3);
    return { ...m, form: { ...m.form, genres: gs } };
  });

  const applyReal = () => setModal((m) => (m ? {
    ...m, form: { ...m.form, genres: m.real.genres, mood: m.real.mood },
  } : m));

  const setFormField = (patch) => setModal((m) => (m ? { ...m, form: { ...m.form, ...patch } } : m));

  const saveModal = async () => {
    if (!modal || busy) return;
    const f = modal.form;
    if (!f.title.trim()) { toast.error("Вкажіть назву прикладу"); return; }
    if (!f.genres.length) { toast.error("Оберіть хоча б один жанр"); return; }
    if (!f.mood) { toast.error("Оберіть настрій"); return; }
    setBusy(true);
    try {
      if (modal.kind === "song") {
        const url = modal.sel.item.urls?.[modal.sel.take];
        if (!url) throw new Error("Оберіть варіант треку");
        await api.post("/admin/examples/from-song", {
          url, title: f.title.trim(), show_title: !!f.show_title,
          genres: f.genres, mood: f.mood,
          color: f.color, ref: refOf(modal.sel.item),
        });
        toast.success("Приклад додано ✓");
      } else if (modal.kind === "upload") {
        if (!file) throw new Error("Оберіть аудіофайл");
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", f.title.trim());
        fd.append("show_title", f.show_title ? "1" : "");
        fd.append("genres", JSON.stringify(f.genres));
        fd.append("mood", f.mood);
        fd.append("color", f.color);
        await api.post("/admin/examples/upload", fd);
        toast.success("Приклад додано ✓");
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
      } else {
        await api.post(`/admin/examples/${modal.it.ex_id}`, {
          title: f.title.trim(), show_title: !!f.show_title,
          genres: f.genres, mood: f.mood,
          color: f.color, sort: Number(f.sort) || 0,
        });
        toast.success("Збережено");
      }
      setModal(null);
      await load();
    } catch (e) {
      if (!authFail(e)) toast.error(e?.response?.data?.detail || e?.message || "Не вдалося зберегти");
    } finally { setBusy(false); }
  };

  const toggleActive = async (it) => {
    if (busyId) return;
    setBusyId(it.ex_id);
    try {
      const r = await api.post(`/admin/examples/${it.ex_id}`, { active: !it.active });
      setExamples((s) => ({
        ...s,
        items: s.items.map((x) => (x.ex_id === it.ex_id ? (r.data?.item || { ...x, active: !it.active }) : x)),
      }));
      toast.success(it.active ? "Приклад приховано" : "Приклад активний");
    } catch (e) {
      if (!authFail(e)) toast.error(e?.response?.data?.detail || "Не вдалося змінити статус");
    } finally { setBusyId(""); }
  };

  const move = async (idx, dir) => {
    const items = examples.items;
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setExamples((s2) => ({ ...s2, items: next }));
    try {
      await api.post("/admin/examples/reorder", { order: next.map((x) => x.ex_id) });
    } catch (e) {
      if (!authFail(e)) toast.error(e?.response?.data?.detail || "Не вдалося змінити порядок");
      await load();
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Видалити приклад «${it.title || "—"}»?`)) return;
    setRemovingId(it.ex_id);
    try {
      await api.delete(`/admin/examples/${it.ex_id}`);
      toast.success("Видалено");
      await load();
    } catch (e) {
      if (!authFail(e)) toast.error(e?.response?.data?.detail || "Не вдалося видалити");
    } finally { setRemovingId(""); }
  };

  const genreChoices = meta.genres.filter((g) => g.key !== "mix");
  const saveLabel = modal?.kind === "edit" ? "Зберегти" : modal?.kind === "upload" ? "Завантажити" : "Додати приклад";

  return (
    <div className="space-y-5" data-testid="admin-examples">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-[#94A3B8]">
          Всього прикладів: <b className="text-white">{examples.total}</b>
        </div>
        <div className="text-xs text-[#64748B]">
          Керуй прикладами звучання на головній сторінці застосунку
        </div>
      </div>

      <div className="card-soft rounded-2xl p-4 space-y-4">
        <div className="inline-flex rounded-xl border border-white/10 bg-black/20 p-1">
          {[["song", "З пісень"], ["upload", "Файлом"]].map(([key, label]) => (
            <button key={key} data-testid={`examples-mode-${key}`} onClick={() => setMode(key)}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                mode === key ? "adm-grad-cta text-white" : "text-[#94A3B8] hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>

        {mode === "song" ? (
          <div className="space-y-3">
            <div className="relative max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Пошук пісні за назвою…"
                className="adm-input w-full h-10 pl-9 pr-3 text-sm" />
            </div>

            {songs.loading && <div className="text-[#64748B] text-sm py-2">Завантаження…</div>}

            <div className="space-y-2 max-h-[560px] overflow-y-auto adm-scroll pr-1">
              {songs.items.map((s) => (
                <div key={s.id} className="rounded-2xl border border-white/10 p-3" style={{ background: "rgba(11,23,41,.55)" }}>
                  <div className="flex items-center gap-3">
                    <CoverBox url={s.cover_url} className="w-12 h-12 rounded-xl shrink-0" iconSize={20} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-white truncate">{s.title || "Без назви"}</div>
                      <div className="text-[11px] text-[#64748B] truncate">
                        {s.username ? "@" + s.username : (s.first_name || "—")} · {s.tg_id || "—"}
                        {s.created_at ? ` · ${new Date(s.created_at).toLocaleDateString("uk-UA")}` : ""}
                      </div>
                    </div>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-[#94A3B8] shrink-0">
                      {genreLabel(s.genre)}
                    </span>
                  </div>

                  <div className="mt-2.5 space-y-1.5">
                    {(s.urls || []).map((u, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-xl px-2 py-1.5 border border-transparent">
                        <button onClick={() => pickVariant(s, i)} title="Обрати цей варіант"
                          data-testid={`examples-pick-${s.id}-${i}`}
                          className="shrink-0 w-8 h-8 rounded-full border border-white/15 grid place-items-center text-[#8CCBFF] hover:border-[#2498FF] hover:bg-[#2498FF]/10 transition-colors">
                          <Play size={12} fill="currentColor" />
                        </button>
                        <button onClick={() => pickVariant(s, i)}
                          className="text-[11px] text-left shrink-0 max-w-[42%] truncate text-[#94A3B8] hover:text-white transition-colors"
                          title={s.take_titles?.[i] || ""}>
                          {s.urls?.length > 1 ? (s.take_titles?.[i] || `Варіант ${i + 1}`) : (s.take_titles?.[i] || "Трек")}
                        </button>
                        <audio controls preload="none" src={u} className="h-8 flex-1 min-w-0" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {!songs.loading && !songs.items.length && (
              <div className="text-center py-6 text-[#475569] text-sm">Нічого не знайдено</div>
            )}

            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-xs text-[#64748B] font-mono">стор. {songs.page + 1}</span>
              <div className="flex items-center gap-2 text-sm">
                <button disabled={songs.page <= 0 || songs.loading} onClick={() => loadSongs(songs.page - 1, q)}
                  className="px-3 py-1.5 rounded-lg border border-white/10 text-[#CBD5E1] disabled:opacity-40 hover:bg-white/5">
                  ← Назад
                </button>
                <button disabled={!songs.hasMore || songs.loading} onClick={() => loadSongs(songs.page + 1, q)}
                  className="px-3 py-1.5 rounded-lg border border-white/10 text-[#CBD5E1] disabled:opacity-40 hover:bg-white/5">
                  Вперед →
                </button>
              </div>
            </div>

            <div className="text-xs text-[#64748B]">
              Натисни ▶ біля треку — відкриється картка заповнення: назва, жанр (можна мікс), настрій, колір.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <input ref={fileRef} type="file" accept=".mp3,.m4a,.aac,.ogg,.wav,.opus,audio/*" onChange={pickFile}
              className="block w-full text-xs text-[#94A3B8] cursor-pointer file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-[#2BA8FF]/15 file:text-[#8CCBFF] file:text-xs file:font-semibold" />
            <div className="text-[11px] text-[#475569]">
              MP3, M4A, AAC, OGG, WAV, OPUS · до 25 МБ
            </div>
            {file && (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-xs text-[#94A3B8] truncate">
                  Файл: <b className="text-white">{file.name}</b> ({(file.size / 1024 / 1024).toFixed(1)} МБ)
                </div>
                <button onClick={resumeUpload}
                  className="text-xs bg-white/5 text-[#8CCBFF] border border-white/10 rounded-lg px-2.5 py-1 hover:bg-white/10 transition-colors">
                  Заповнити дані →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-[11px] font-mono uppercase tracking-wider text-[#475569]">Поточні приклади</div>
        {examples.loading && <div className="text-[#64748B] text-sm py-2">Завантаження…</div>}
        {!examples.loading && !examples.items.length && (
          <div className="card-soft rounded-2xl p-6 text-center text-[#64748B] text-sm">Ще немає прикладів звучання</div>
        )}
        {examples.items.map((it, idx) => (
          <div key={it.ex_id} data-testid={`examples-row-${it.ex_id}`}
            className={`card-soft rounded-2xl p-4 transition-opacity ${it.active ? "" : "opacity-50"}`}>
            <audio controls preload="none" src={it.url} className="h-8 w-full min-w-0" />
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="font-bold text-white text-sm truncate">«{it.title || "Без назви"}»</span>
              {(it.genre_labels?.length ? it.genre_labels : [it.genre_label]).filter(Boolean).length > 0 && (
                <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(22,184,255,.14)", color: PLAYER.blue }}>
                  <span className="w-1.5 h-1.5 rounded-full"
                    style={{ background: it.color || genreColor(it.genre) }} />
                  {(it.genre_labels?.length ? it.genre_labels : [it.genre_label]).filter(Boolean).join(" + ")}
                </span>
              )}
              {it.mood_label && (
                <span className="text-[11px] px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(255,216,61,.14)", color: PLAYER.yellow }}>
                  {it.mood_label}
                </span>
              )}
              {it.duration > 0 && (
                <span className="text-[11px] text-[#64748B]">
                  {`${Math.floor(it.duration / 60)}:${String(Math.floor(it.duration % 60)).padStart(2, "0")}`}
                </span>
              )}
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-[#94A3B8]">
                {it.source === "song" ? "З пісні" : "Файл"}
              </span>
              {it.src_ref && (
                <span className="text-[11px] text-[#64748B] truncate max-w-[220px]" title={it.src_ref}>{it.src_ref}</span>
              )}
              <span className="ml-auto flex items-center gap-2">
                <span className="flex items-center gap-0.5 mr-1">
                  <button data-testid={`examples-up-${it.ex_id}`} onClick={() => move(idx, -1)}
                    disabled={idx === 0} title="Перемістити вище"
                    className="text-[#94A3B8] hover:text-white disabled:opacity-25 px-1 py-2">
                    <ChevronUp size={15} />
                  </button>
                  <button data-testid={`examples-down-${it.ex_id}`} onClick={() => move(idx, 1)}
                    disabled={idx === examples.items.length - 1} title="Перемістити нижче"
                    className="text-[#94A3B8] hover:text-white disabled:opacity-25 px-1 py-2">
                    <ChevronDown size={15} />
                  </button>
                </span>
                <button data-testid={`examples-toggle-${it.ex_id}`} onClick={() => toggleActive(it)}
                  disabled={busyId === it.ex_id}
                  title={it.active ? "Натисніть, щоб приховати" : "Натисніть, щоб показати"}
                  className={`text-xs border rounded-lg px-2.5 py-1 transition-colors ${it.active
                    ? "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30 hover:bg-[#10B981]/25"
                    : "bg-white/5 text-[#94A3B8] border-white/10 hover:bg-white/10"}`}>
                  {busyId === it.ex_id ? "…" : (it.active ? "Активний" : "Прихований")}
                </button>
                <button data-testid={`examples-edit-${it.ex_id}`} onClick={() => startEdit(it)}
                  className="text-xs bg-white/5 text-[#94A3B8] border border-white/10 rounded-lg px-2.5 py-1 hover:bg-white/10 transition-colors">
                  Редагувати
                </button>
                <button data-testid={`examples-delete-${it.ex_id}`} onClick={() => remove(it)}
                  disabled={removingId === it.ex_id} title="Видалити"
                  className="text-[#EF4444] hover:text-[#F87171] px-1 disabled:opacity-40">
                  {removingId === it.ex_id ? "…" : <Trash2 size={14} />}
                </button>
              </span>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[140] flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="examples-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#0B1729] p-5 space-y-4 shadow-2xl max-h-[92vh] overflow-y-auto adm-scroll">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold text-base">
                {modal.kind === "edit" ? "Редагувати приклад" : "Новий приклад"}
              </h3>
              <button onClick={closeModal} className="text-[#64748B] hover:text-white" aria-label="Закрити">
                <X size={18} />
              </button>
            </div>

            {modal.kind === "song" && (
              <div className="text-xs text-[#64748B] -mt-2 leading-relaxed">
                «{modal.sel.item.title || "Без назви"}»
                {modal.sel.item.urls?.length > 1 ? ` · ${modal.sel.item.take_titles?.[modal.sel.take] || `Варіант ${modal.sel.take + 1}`}` : ""}
                {modal.real.genres.length || modal.real.mood ? (
                  <> · з треку: <b className="text-[#94A3B8]">
                    {[modal.real.genres.map((g) => genreLabel(g)).join(" + "), meta.moods.find((m) => m.key === modal.real.mood)?.label].filter(Boolean).join(" · ")}
                  </b></>
                ) : null}
              </div>
            )}
            {modal.kind === "upload" && file && (
              <div className="text-xs text-[#64748B] -mt-2 truncate">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} МБ</div>
            )}

            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-[#475569]">Назва прикладу</label>
              <input value={modal.form.title} maxLength={80} disabled={busy}
                onChange={(e) => setFormField({ title: e.target.value })}
                data-testid="exm-title"
                placeholder="Наприклад: Лірика під гітару"
                className="adm-input w-full h-10 px-3 text-sm mt-1.5" />
              <label className="flex items-center gap-2 select-none cursor-pointer mt-3">
                <input type="checkbox" checked={!!modal.form.show_title} disabled={busy}
                  onChange={(e) => setFormField({ show_title: e.target.checked })}
                  data-testid="exm-show-title"
                  className="w-4 h-4 accent-[#2498FF]" />
                <span className="text-xs text-[#94A3B8]">Показувати назву в картці
                  <span className="text-[#475569]"> — після жанрів, до 10 символів</span></span>
              </label>
            </div>

            <div>
              <div className="flex items-center justify-between gap-2">
                <label className="text-[11px] font-mono uppercase tracking-wider text-[#475569]">Жанр · можна мікс (до 3)</label>
                {modal.kind === "song" && (modal.real.genres.length > 0 || modal.real.mood) && (
                  <button type="button" onClick={applyReal}
                    data-testid="exm-real"
                    className="text-[11px] text-[#38A8FF] hover:underline shrink-0">
                    Стандартний варіант
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 mt-2">
                {genreChoices.map((g) => {
                  const on = modal.form.genres.includes(g.key);
                  return (
                    <button key={g.key} type="button" disabled={busy}
                      data-testid={`exm-genre-${g.key}`}
                      onClick={() => toggleGenre(g.key)}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-[11px] font-semibold transition-colors ${
                        on ? "border-[#2498FF] bg-[#2498FF]/15 text-white"
                          : "border-white/10 bg-white/[.03] text-[#94A3B8] hover:border-white/25 hover:text-white"}`}>
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: genreColor(g.key) }} />
                      <span className="truncate">{g.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-[#64748B] mt-2">
                У картці: <b className="text-[#CBD5E1]">{[modalGenreLabels(modal.form) || "—",
                  modal.form.show_title && modal.form.title.trim() ? truncName(modal.form.title) : ""]
                  .filter(Boolean).join(" · ")}</b>
                {modal.form.genres.length > 1 ? " (мікс)" : ""}
              </div>
            </div>

            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-[#475569]">Настрій</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-2">
                {meta.moods.map((m) => {
                  const on = modal.form.mood === m.key;
                  return (
                    <button key={m.key} type="button" disabled={busy}
                      data-testid={`exm-mood-${m.key}`}
                      onClick={() => setFormField({ mood: m.key })}
                      className={`px-2 py-1.5 rounded-lg border text-[11px] font-semibold transition-colors ${
                        on ? "border-[#2498FF] bg-[#2498FF]/15 text-white"
                          : "border-white/10 bg-white/[.03] text-[#94A3B8] hover:border-white/25 hover:text-white"}`}>
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-[#475569]">Колір хвилі</label>
                <div className="flex items-center gap-2 mt-2">
                  <input type="color" disabled={busy} data-testid="exm-color"
                    value={modal.form.color || genreColor(modal.form.genres[0] || "")}
                    onChange={(e) => setFormField({ color: e.target.value })}
                    className="w-10 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer" />
                  <button type="button" disabled={busy} onClick={() => setFormField({ color: "" })}
                    className={`h-10 px-3 rounded-lg border text-xs font-semibold transition-colors ${
                      modal.form.color ? "border-white/10 text-[#94A3B8] hover:bg-white/5"
                        : "border-[#2498FF]/60 text-[#8CCBFF] bg-[#2498FF]/10"}`}>
                    Авто
                  </button>
                </div>
                <div className="text-[10px] text-[#475569] mt-1">Авто — колір жанру, або свій</div>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-[#475569]">Порядок</label>
                <input type="number" value={modal.form.sort} disabled={busy}
                  onChange={(e) => setFormField({ sort: e.target.value })}
                  className="adm-input w-full h-10 px-3 text-sm mt-2" />
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button onClick={closeModal} disabled={busy}
                className="flex-1 h-10 rounded-xl border border-white/10 text-sm text-[#94A3B8] hover:bg-white/5 transition-colors">
                Скасувати
              </button>
              <button data-testid="exm-save" onClick={saveModal} disabled={busy}
                className="adm-grad-cta flex-1 h-10 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-40">
                {busy ? <RefreshCw size={14} className="animate-spin" /> : null}
                {busy ? "Зберігаю…" : saveLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlayerOverlay({ item, initialTake = 0, onClose, onChanged }) {
  const tracks = item.tracks || [];
  const [take, setTake] = useState(initialTake);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [cover, setCover] = useState(item.cover_url);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(item.take_titles?.[0] || item.title || "");
  const [saving, setSaving] = useState(false);
  const audioRef = useRef(null);
  const fileRef = useRef(null);
  const barRef = useRef(null);
  const playingRef = useRef(false);
  const cancelEdit = useRef(false);

  const src = tracks[take] || tracks[0];

  useEffect(() => { playingRef.current = playing; }, [playing]);
  useEffect(() => { setTitle(item.take_titles?.[take] || item.title || ""); }, [take, item]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    setTime(0); setDur(0);
    a.load();
    if (playingRef.current) a.play().catch(() => {});
    // eslint-disable-next-line
  }, [take, src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {}); else a.pause();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.code === "Space" && !editing) { e.preventDefault(); toggle(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [editing, onClose, playing]);

  const seekTo = (clientX) => {
    const bar = barRef.current, a = audioRef.current;
    if (!bar || !a || !dur) return;
    const r = bar.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - r.left, 0), r.width);
    a.currentTime = (x / r.width) * dur;
    setTime(a.currentTime);
  };

  const onPickCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await squareJpeg(f, 900);
      const r = await api.post(`/admin/songs/${item.id}/cover`, { image: dataUrl });
      setCover(r.data.cover_url);
      onChanged({ cover_url: r.data.cover_url });
      toast.success("Обкладинку оновлено");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не вдалося завантажити обкладинку");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveTitle = async () => {
    const t = title.trim();
    const current = item.take_titles?.[take] || item.title || "";
    if (t === current || t.length < 2) { setEditing(false); setTitle(current); return; }
    try {
      setSaving(true);
      const r = await api.patch(`/admin/songs/${item.id}/title`, { title: t, take });
      const titles = [...(item.take_titles || [])];
      while (titles.length <= take) titles.push(item.title || "");
      titles[take] = r.data.title;
      onChanged({ take_titles: titles, title: take === 0 ? r.data.title : item.title });
      toast.success("Назву збережено");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не вдалося зберегти назву");
    } finally { setSaving(false); setEditing(false); }
  };

  const pct = dur ? Math.min(100, (time / dur) * 100) : 0;

  return (
    <div className="fixed inset-0 z-[100]"
      style={{ background: "radial-gradient(1100px 700px at 50% -12%, #12283F 0%, #050A14 55%, #050A14 100%)" }}>
      <button onClick={onClose} data-testid="player-close"
        className="absolute right-4 z-10 w-10 h-10 rounded-full grid place-items-center border border-white/10 text-[#94A3B8] hover:text-white hover:bg-white/5 transition-colors"
        style={{ top: "max(1rem, env(safe-area-inset-top))" }}>
        <X size={18} />
      </button>

      <div className="h-full overflow-y-auto no-scrollbar flex">
        <div className="m-auto w-full max-w-[380px] px-5 sm:px-6 pt-[50px] pb-4 flex flex-col items-center">
          <button onClick={() => fileRef.current?.click()}
            className="group relative w-[240px] h-[240px] sm:w-[280px] sm:h-[280px] rounded-[24px] overflow-hidden"
            style={{ boxShadow: "0 30px 80px -24px rgba(22,184,255,.45)" }}
            title="Натисніть, щоб завантажити обкладинку">
            <CoverBox url={cover} className="w-full h-full" iconSize={48} />
            <span className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: "rgba(5,10,20,.5)" }}>
              <span className="flex items-center gap-2 text-sm font-semibold text-white">
                <Camera size={18} /> Замінити обкладинку
              </span>
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickCover} />

          <WaveBars className="mt-5" active={playing} scale={0.7} />

          <div className="mt-4 w-full text-center">
            {editing ? (
              <input value={title} autoFocus disabled={saving} maxLength={60}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") e.currentTarget.blur();
                  if (e.key === "Escape") { cancelEdit.current = true; e.currentTarget.blur(); }
                }}
                onBlur={() => {
                  if (cancelEdit.current) {
                    cancelEdit.current = false;
                    setTitle(item.take_titles?.[take] || item.title || "");
                    setEditing(false);
                    return;
                  }
                  saveTitle();
                }}
                className="w-full text-center text-2xl font-extrabold text-white bg-transparent outline-none border-b-2 pb-1"
                style={{ fontFamily: "Manrope, Inter, sans-serif", borderColor: PLAYER.blue }} />
            ) : (
              <button onClick={() => setEditing(true)} title="Натисніть, щоб переписати назву"
                className="inline-block max-w-full">
                <span className="text-xl sm:text-2xl font-extrabold text-white truncate"
                  style={{ fontFamily: "Manrope, Inter, sans-serif" }}>
                  {item.take_titles?.[take] || item.title || "Без назви"}
                </span>
              </button>
            )}
          </div>

          <div className="mt-5 w-full">
            <div ref={barRef}
              onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); seekTo(e.clientX); }}
              onPointerMove={(e) => { if (e.buttons === 1) seekTo(e.clientX); }}
              className="relative h-1.5 rounded-full bg-white/10 cursor-pointer touch-none">
              <div className="absolute inset-y-0 left-0 rounded-full"
                style={{ width: `${pct}%`, background: "linear-gradient(90deg,#16B8FF,#FFD83D)" }} />
              <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow"
                style={{ left: `calc(${pct}% - 6px)` }} />
            </div>
            <div className="mt-1.5 flex justify-between text-[11px] text-[#64748B] tabular-nums">
              <span>{fmtTime(time)}</span>
              <span>{fmtTime(dur)}</span>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-6">
            <button disabled={take <= 0} onClick={() => setTake(Math.max(0, take - 1))}
              className="text-[#94A3B8] hover:text-white disabled:opacity-30 transition-colors"
              title="Попередній варіант">
              <SkipBack size={22} />
            </button>
            <button onClick={toggle} data-testid="player-toggle"
              className="w-14 h-14 rounded-full grid place-items-center text-white transition-transform active:scale-95"
              style={{ background: `linear-gradient(135deg, ${PLAYER.blue}, ${PLAYER.action})`, boxShadow: "0 0 50px rgba(22,184,255,.45)" }}>
              {playing ? <Pause size={22} fill="white" /> : <Play size={22} fill="white" className="ml-0.5" />}
            </button>
            <button disabled={take >= tracks.length - 1}
              onClick={() => setTake(Math.min(tracks.length - 1, take + 1))}
              className="text-[#94A3B8] hover:text-white disabled:opacity-30 transition-colors"
              title="Наступний варіант">
              <SkipForward size={22} />
            </button>
          </div>

          <audio ref={audioRef} src={src} preload="metadata"
            onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
            onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
            onLoadedMetadata={(e) => setDur(e.currentTarget.duration || 0)}
            onEnded={() => setPlaying(false)} />
        </div>
      </div>
    </div>
  );
}

function UsersTable({ users, meta, onPage, onGrant, onBan, seed, onSeedHandled }) {
  const risk = { high: "text-[#EF4444]", medium: "text-[#EAB308]", low: "text-[#10B981]" };
  const page = meta?.page ?? 0;
  const pages = meta?.pages ?? 1;
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (seed) {
      setFilter(seed);
      onSeedHandled?.();
    }
    // eslint-disable-next-line
  }, [seed]);

  const q = filter.trim().toLowerCase();
  const shown = q
    ? users.filter((u) =>
        `${u.telegram_id || ""} ${u.username || ""} ${u.first_name || ""}`.toLowerCase().includes(q))
    : users;

  return (
    <div className="card-soft rounded-2xl overflow-hidden" data-testid="admin-users-table">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-white/10">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Пошук за @юзернейм або ID"
            className="adm-input w-full h-10 pl-9 pr-3 text-sm" />
        </div>
        <span className="text-xs text-[#64748B]">Знайдено: {shown.length}</span>
      </div>
      <div className="overflow-x-auto no-scrollbar">
        <table className="w-full min-w-[720px]">
          <thead className="border-b border-white/10"><tr><Th>Telegram ID</Th><Th>Юзернейм</Th><Th>Мова</Th><Th>Баланс</Th><Th>Реферали</Th><Th>Витрачено</Th><Th>Ризик</Th><Th>Генерації</Th><Th>Доступ</Th></tr></thead>
          <tbody>
            {shown.length ? shown.map((u) => (
              <tr key={u.telegram_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <Td className="font-mono text-[#94A3B8]">
                  {u.is_banned ? <span className="text-[#EF4444] mr-1" title="заблокований">🚫</span> : null}
                  {u.telegram_id}
                </Td>
                <Td>@{u.username || "—"}</Td>
                <Td className="uppercase font-mono text-xs">{u.language}</Td>
                <Td className="font-bold">{u.balance ?? 0}</Td>
                <Td>{u.referral_count ?? 0}</Td>
                <Td className="text-[#10B981]">{u.total_spent ?? 0}₴</Td>
                <Td className={`font-medium ${risk[u.fraud_risk]}`}>{u.fraud_risk}</Td>
                <Td>
                  <button data-testid={`grant-${u.telegram_id}`} onClick={() => onGrant(u.telegram_id)}
                    className="text-xs bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#10B981]/25 transition-colors">
                    + генерації
                  </button>
                </Td>
                <Td>
                  <button data-testid={`ban-${u.telegram_id}`} onClick={() => onBan(u.telegram_id, !u.is_banned)}
                    className={`text-xs border rounded-lg px-2.5 py-1.5 transition-colors ${u.is_banned
                      ? "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/30 hover:bg-[#EF4444]/25"
                      : "bg-white/5 text-[#94A3B8] border-white/10 hover:border-[#EF4444]/50"}`}>
                    {u.is_banned ? "Розблокувати" : "Заблокувати"}
                  </button>
                </Td>
              </tr>
            )) : <tr><td colSpan={9} className="py-12 text-center text-[#64748B]">Ще немає користувачів. Напишіть боту в Telegram, щоб побачити дані.</td></tr>}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
          <span className="text-xs text-[#64748B] font-mono">
            {meta.total} користувачів · стор. {page + 1}/{pages}
          </span>
          <div className="flex gap-2">
            <button data-testid="users-prev" disabled={page <= 0} onClick={() => onPage(page - 1)}
              className="text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 disabled:opacity-30 hover:border-[#F97316]/50 transition-colors">
              ← Назад
            </button>
            <button data-testid="users-next" disabled={page >= pages - 1} onClick={() => onPage(page + 1)}
              className="text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 disabled:opacity-30 hover:border-[#F97316]/50 transition-colors">
              Далі →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const ORDER_STATUS = {
  created: ["text-[#94A3B8]", "створено"],
  screenshot_wait: ["text-[#EAB308]", "чекає скрін"],
  unpaid: ["text-[#FFD400]", "без оплати"],
  pending: ["text-[#F97316]", "на перевірці"],
  paid: ["text-[#10B981]", "оплачено"],
  in_progress: ["text-[#A855F7]", "на виконанні"],
  done: ["text-[#38BDF8]", "виконано"],
  expired: ["text-[#EF4444]", "прострочено"],
  rejected: ["text-[#EF4444]", "відхилено"],
};

const AGENT_STATUSES = [
  ["all", "Всі"], ["in_progress", "На виконанні"], ["paid", "Оплачено"],
  ["pending", "На перевірці"], ["unpaid", "Без оплати"], ["done", "Виконано"],
  ["admin", "🎛 Адмінські"],
];
const AGENT_RANK = {
  in_progress: 0, paid: 1, pending: 2, screenshot_wait: 2, created: 3,
  unpaid: 3, done: 8, expired: 9, rejected: 9,
};

function agentRunsOf(order) {
  const gen = order.gen || {};
  const runs = Array.isArray(gen.runs) ? gen.runs : [];
  if (runs.length) return runs;
  if (gen.task_id || gen.result_urls) {
    return [{
      run_id: "legacy", task_id: gen.task_id, urls: gen.result_urls,
      titles: null, title: gen.title || "", started_at: gen.started_at,
      finished_at: gen.finished_at, error: gen.error, primary: !!gen.result_urls,
    }];
  }
  return [];
}

async function downloadTrack(oid, runId, idx, title) {
  const safe = (title || "track").replace(/[\\/:*?"<>|]/g, "").trim() || "track";
  try {
    const r = await api.get(
      `/admin/orders/${oid}/runs/${runId}/tracks/${idx}/download`,
      { responseType: "blob" });
    const blob = new Blob([r.data], { type: "audio/mpeg" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${safe}.mp3`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  } catch {
    toast.error("Не вдалося скачати трек");
  }
}

function OrderAgentModal({ order, onClose, onAction, onOpenAttachment }) {
  const [opts, setOpts] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [runs, setRuns] = useState(() => agentRunsOf(order));
  const [shareUrl, setShareUrl] = useState(
    order.share_code ? `${window.location.origin}/s/${order.share_code}` : "");
  const [form, setForm] = useState(() => ({
    genre: order.landing_genre || "pop",
    mood: "",
    voice: order.landing_voice || "female",
    song_language: order.gen?.lang || "uk",
    title: order.gen?.title || "",
    prompt: order.gen?.prompt || order.landing_description || "",
    lyrics: order.gen?.lyrics || "",
  }));

  useEffect(() => {
    api.get("/admin/genres").then((r) => setOpts(r.data)).catch(() => {});
  }, []);

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Скопійовано ✓");
    } catch { toast.error("Не вдалося скопіювати"); }
  };

  const generate = async () => {
    if (busy) return;
    setBusy("llm"); setErr("");
    try {
      const r = await api.post(`/admin/orders/${order._id}/lyrics`, {
        genre: form.genre, mood: form.mood, voice: form.voice,
        song_language: form.song_language, prompt: form.prompt,
      });
      setForm((f) => ({ ...f, title: r.data.title || f.title, lyrics: r.data.lyrics || "" }));
      toast.success("Текст згенеровано — можна редагувати");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Помилка генерації тексту");
    } finally { setBusy(""); }
  };

  const start = async () => {
    if (busy) return;
    setBusy("start"); setErr("");
    try {
      await api.post(`/admin/orders/${order._id}/start`, {
        genre: form.genre, mood: form.mood, voice: form.voice,
        song_language: form.song_language, prompt: form.prompt,
        title: form.title, lyrics: form.lyrics,
      });
      toast.success("Відправлено на генерацію 🎶");
      await onAction(order._id, "refresh");
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Не вдалося відправити");
    } finally { setBusy(""); }
  };

  const act = async (a) => {
    await onAction(order._id, a);
    onClose();
  };

  const createShare = async () => {
    try {
      const r = await api.post(`/admin/orders/${order._id}/share`);
      setShareUrl(r.data.url);
      toast.success("Посилання створено 🔗");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не вдалося створити посилання");
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Скопійовано ✓");
    } catch { toast.error("Не вдалося скопіювати"); }
  };

  const setRunTitles = async (run, titles) => {
    setRuns((rs) => rs.map((r) => (r.run_id === run.run_id ? { ...r, titles } : r)));
    try {
      await api.patch(`/admin/orders/${order._id}/runs/${run.run_id}`, { titles });
      toast.success("Назву збережено");
    } catch { toast.error("Не вдалося зберегти назву"); }
  };

  const makePrimary = async (run) => {
    try {
      await api.patch(`/admin/orders/${order._id}/runs/${run.run_id}`, { primary: true });
      setRuns((rs) => rs.map((r) => ({ ...r, primary: r.run_id === run.run_id })));
      toast.success("Основний результат оновлено");
    } catch { toast.error("Не вдалося змінити основний"); }
  };

  const deliver = async () => {
    if (busy) return;
    setBusy("deliver"); setErr("");
    try {
      const r = await api.post(`/admin/orders/${order._id}/deliver`);
      toast.success(`Надіслано клієнту: ${r.data.sent} треків 🎧`);
      await onAction(order._id, "refresh");
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Не вдалося надіслати клієнту");
    } finally { setBusy(""); }
  };

  const [cls, label] = ORDER_STATUS[order.status] || ["text-[#94A3B8]", order.status];

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-3xl bg-[#0F1729] border-white/10 text-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap text-base">
            <span>{order.number ? `#${order.number}` : (order.source === "admin" ? "🎛 конструктор" : "🌐 чернетка")}</span>
            <span className={`text-xs px-2.5 py-1 rounded-full border border-current/25 ${cls}`}>{label}</span>
            <span className="text-[#10B981] font-display">{order.amount || 200}₴</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-2xl border border-white/10 p-4 grid sm:grid-cols-2 gap-3">
            <Field label="Звучання" value={order.landing_genre_label || order.package_name || "—"} />
            <Field label="Голос" value={order.landing_voice_label || "—"} />
            <Field label="Куди надіслати" value={order.landing_contact
              ? `${order.landing_delivery_label || ""} · ${order.landing_contact}`
              : (order.user_telegram_id ? `TG ${order.user_telegram_id}` : "—")} />
            <Field label="Дата" value={(order.created_at || "").slice(0, 16).replace("T", " ")} mono />
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#64748B]">Опис від клієнта</div>
                <button onClick={() => copyText(order.landing_description || "")}
                  className="text-xs px-2 py-0.5 rounded border border-white/10 hover:bg-white/5">📋 Копіювати</button>
              </div>
              <p className="text-sm text-[#CBD5E1] mt-1 whitespace-pre-wrap break-words">{order.landing_description || "—"}</p>
            </div>
            <div className="sm:col-span-2 flex flex-wrap items-center gap-2 pt-1">
              {order.landing_receipt_path ? (
                <button onClick={() => onOpenAttachment(order, "receipt")}
                  className="text-xs bg-[#2BA8FF]/10 text-[#7CC5FF] border border-[#2BA8FF]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#2BA8FF]/20">🖼 Чек</button>
              ) : order.screenshot_file_id ? (
                <span className="text-xs text-[#94A3B8]">✓ Чек у Telegram</span>
              ) : <span className="text-xs text-[#64748B]">Чека немає</span>}
              {order.landing_story_path && (
                <button onClick={() => onOpenAttachment(order, "story")}
                  className="text-xs bg-[#8B5CF6]/10 text-[#B9A5F7] border border-[#8B5CF6]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#8B5CF6]/20">📖 Історія</button>
              )}
              <span className="flex-1" />
              {["pending", "screenshot_wait", "created"].includes(order.status) && (
                <>
                  <button onClick={() => act("approve")}
                    className="text-xs bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#10B981]/25">✅ Підтвердити оплату</button>
                  <button onClick={() => act("reject")}
                    className="text-xs bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#EF4444]/20">❌ Відхилити</button>
                </>
              )}
              {["unpaid", "pending"].includes(order.status) && (
                <button onClick={() => act("paid")}
                  className="text-xs bg-[#FFD400]/10 text-[#FFD400] border border-[#FFD400]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#FFD400]/20">💰 Позначити оплаченим</button>
              )}
              {["paid", "in_progress"].includes(order.status) && (
                <button onClick={() => act("done")}
                  className="text-xs bg-[#38BDF8]/10 text-[#38BDF8] border border-[#38BDF8]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#38BDF8]/20">🏁 Позначити виконаним</button>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 p-4 space-y-3">
            <div className="text-sm font-bold text-white">🎛 Налаштування генерації</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <select value={form.genre} onChange={(e) => setForm((f) => ({ ...f, genre: e.target.value }))}
                className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none">
                {(opts?.genres || []).map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
              </select>
              <select value={form.mood} onChange={(e) => setForm((f) => ({ ...f, mood: e.target.value }))}
                className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none">
                {(opts?.moods || []).map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
              <select value={form.voice} onChange={(e) => setForm((f) => ({ ...f, voice: e.target.value }))}
                className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none">
                {(opts?.voices || []).map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
              </select>
              <select value={form.song_language} onChange={(e) => setForm((f) => ({ ...f, song_language: e.target.value }))}
                className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none">
                {(opts?.langs || []).map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
              </select>
            </div>
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Назва пісні"
              className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none" />
            <textarea value={form.prompt} rows={3} maxLength={3000}
              onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))}
              placeholder="Запит для ШІ: опис клієнта, деталі, імена…"
              className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none resize-y" />
            <button onClick={generate} disabled={!!busy}
              className="text-sm bg-[#A855F7]/15 text-[#C4A5FF] border border-[#A855F7]/40 rounded-lg px-4 py-2 hover:bg-[#A855F7]/25 disabled:opacity-50">
              {busy === "llm" ? "⏳ Генерую текст…" : "🪄 Згенерувати текст (ШІ)"}
            </button>
            <textarea value={form.lyrics} rows={12}
              onChange={(e) => setForm((f) => ({ ...f, lyrics: e.target.value }))}
              placeholder="Тут з'явиться текст пісні — можна редагувати вручну без повторної перевірки ШІ"
              className="w-full px-3 py-3 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none resize-y" />
            <button onClick={start} disabled={!!busy || form.lyrics.trim().length < 40}
              className="w-full text-sm font-bold bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/40 rounded-lg px-4 py-2.5 hover:bg-[#10B981]/25 disabled:opacity-40">
              {busy === "start" ? "⏳ Відправляю…" : (runs.some((r) => (r.urls || []).length)
                ? "🚀 Перегенерувати (нова версія)" : "🚀 Відправити на генерацію")}
            </button>
          </div>

          {runs.length > 0 && (
            <div className="rounded-2xl border border-white/10 p-4 space-y-3">
              <div className="text-sm font-bold text-white">🎧 Генерації ({runs.length})</div>
              {[...runs].reverse().map((r) => (
                <div key={r.run_id}
                  className={`rounded-xl border p-3 space-y-2 ${r.primary ? "border-[#10B981]/40 bg-[#10B981]/[0.04]" : "border-white/10"}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-[#94A3B8] font-mono">
                      {(r.started_at || "").slice(0, 16).replace("T", " ")}
                    </span>
                    {r.primary && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30">основний</span>
                    )}
                    {r.error ? (
                      <span className="text-[10px] text-[#FF8A8A]">помилка</span>
                    ) : (r.urls || []).length ? (
                      <span className="text-[10px] text-[#94A3B8]">{r.urls.length} варіанти</span>
                    ) : (
                      <span className="text-[10px] text-[#A855F7]">рендериться…</span>
                    )}
                    {!r.primary && (r.urls || []).length > 0 && (
                      <button onClick={() => makePrimary(r)}
                        className="ml-auto text-xs bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30 rounded-lg px-2.5 py-1 hover:bg-[#10B981]/20">
                        Зробити основним
                      </button>
                    )}
                  </div>
                  {r.error && <p className="text-xs text-[#FF8A8A]">{r.error}</p>}
                  {(r.urls || []).map((u, i) => {
                    const title = (r.titles || [])[i] || r.title || `Варіант ${i + 1}`;
                    return (
                      <div key={i} className="space-y-1">
                        <div className="flex items-center gap-2">
                          <input defaultValue={title}
                            placeholder={`Назва варіанту ${i + 1}`}
                            onBlur={(e) => {
                              const next = [...(r.titles || [])];
                              while (next.length < (r.urls || []).length) next.push("");
                              next[i] = e.target.value.trim();
                              setRunTitles(r, next);
                            }}
                            className="flex-1 px-2.5 py-1.5 rounded-lg bg-black/30 border border-white/10 text-xs text-white outline-none focus:border-white/30" />
                          <button onClick={() => downloadTrack(order._id, r.run_id, i, title)}
                            className="text-xs px-2.5 py-1.5 rounded-lg border border-white/10 text-[#94A3B8] hover:text-white hover:border-white/30">
                            ⬇️ Скачати
                          </button>
                        </div>
                        <audio controls src={u} className="w-full h-9" />
                      </div>
                    );
                  })}
                </div>
              ))}

              <div className="rounded-xl border border-white/10 p-3 space-y-2">
                <div className="text-xs font-bold text-white">🔗 Посилання для клієнта (основний результат)</div>
                {shareUrl ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="flex-1 min-w-0 truncate text-xs text-[#8CCBFF]">{shareUrl}</code>
                    <button onClick={copyShare}
                      className="text-xs px-2.5 py-1 rounded-lg border border-white/10 hover:bg-white/5">📋 Копіювати</button>
                    <a href={shareUrl} target="_blank" rel="noreferrer"
                      className="text-xs px-2.5 py-1 rounded-lg border border-white/10 hover:bg-white/5">Відкрити</a>
                  </div>
                ) : (
                  <button onClick={createShare}
                    className="text-xs bg-[#2BA8FF]/10 text-[#7CC5FF] border border-[#2BA8FF]/30 rounded-lg px-3 py-1.5 hover:bg-[#2BA8FF]/20">
                    Створити посилання
                  </button>
                )}
                <p className="text-[10px] text-[#64748B]">
                  Сторінка з плеєром, скачуванням і кнопками «переслати» (Telegram, WhatsApp, Viber).
                </p>
              </div>

              {isTelegramOrder(order) && (
                <div className="rounded-xl border border-[#2BA8FF]/30 p-3 space-y-2">
                  <div className="text-xs font-bold text-[#8CCBFF]">📤 Доставка клієнту в Telegram (від бота)</div>
                  <button onClick={deliver}
                    disabled={busy === "deliver" || !runs.some((r) => (r.urls || []).length)}
                    className="w-full text-sm font-bold bg-[#2BA8FF]/15 text-[#7CC5FF] border border-[#2BA8FF]/40 rounded-lg px-4 py-2.5 hover:bg-[#2BA8FF]/25 disabled:opacity-40">
                    {busy === "deliver" ? "⏳ Надсилаю…" : "Надіслати клієнту: «Ваша пісня готова» + треки"}
                  </button>
                  {order.delivered_at ? (
                    <p className="text-xs text-[#10B981]">
                      ✅ Доставлено: {(order.delivered_at || "").slice(0, 16).replace("T", " ")}
                    </p>
                  ) : (
                    <p className="text-[10px] text-[#64748B]">
                      Вручну: клієнт отримає стандартне повідомлення та обрані треки від бота.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {err && <p className="text-sm text-[#FF8A8A]">{err}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const AGENT_STATUS_OPTIONS = [
  ["unpaid", "Без оплати"], ["pending", "На перевірці"], ["paid", "Оплачено"],
  ["in_progress", "На виконанні"], ["done", "Виконано"],
];

// Явная подсветка карточек по статусу: фон + рамка, видно сразу.
const STATUS_TINT = {
  unpaid: "bg-[#FFD400]/[0.10] border-[#FFD400]/45",
  pending: "bg-[#F97316]/[0.13] border-[#F97316]/50",
  paid: "bg-[#10B981]/[0.16] border-[#10B981]/55",
  in_progress: "bg-[#A855F7]/[0.15] border-[#A855F7]/55",
  done: "bg-[#38BDF8]/[0.10] border-[#38BDF8]/45",
  screenshot_wait: "bg-[#EAB308]/[0.10] border-[#EAB308]/45",
  created: "bg-white/[0.03] border-white/10",
  expired: "bg-[#EF4444]/[0.07] border-[#EF4444]/30",
  rejected: "bg-[#EF4444]/[0.12] border-[#EF4444]/45",
};


const STATUS_DOT = {
  unpaid: "#FFD400", pending: "#F97316", screenshot_wait: "#EAB308",
  paid: "#10B981", in_progress: "#A855F7", done: "#38BDF8",
  created: "#94A3B8", expired: "#EF4444", rejected: "#EF4444",
};

const SOURCE_BADGE = {
  admin: { label: "🎛 Адмін", cls: "text-[#FF8A8A] border-[#EF4444]/40 bg-[#EF4444]/10" },
  app: { label: "📱 Міні-ап", cls: "text-[#7DE3FF] border-[#22D3EE]/40 bg-[#22D3EE]/10" },
  app_balance: { label: "⚖️ З балансу", cls: "text-[#6EE7B7] border-[#10B981]/40 bg-[#10B981]/10" },
  topup: { label: "💳 Поповнення", cls: "text-[#FDE68A] border-[#EAB308]/40 bg-[#EAB308]/10" },
  site1: { label: "🌐 Сайт · Студія", cls: "text-[#FF8A8A] border-[#EF4444]/50 bg-[#EF4444]/15" },
  site2: { label: "🌐 Сайт · Конструктор", cls: "text-[#C4B5FD] border-[#A855F7]/40 bg-[#A855F7]/10" },
  site: { label: "🌐 Сайт", cls: "text-[#9FB0C7] border-white/20 bg-white/5" },
  tg: { label: "🤖 Бот", cls: "text-[#8CCBFF] border-[#2BA8FF]/40 bg-[#2BA8FF]/10" },
  test: { label: "🧪 Тест", cls: "text-[#94A3B8] border-white/15 bg-white/[0.04]" },
  tgs: { label: "🤖 Бот · Простий", cls: "text-[#8CCBFF] border-[#2BA8FF]/40 bg-[#2BA8FF]/10" },
  other: { label: "❔ Невідомо", cls: "text-[#94A3B8] border-white/10 bg-white/5" },
};

function OrdersBoard({ orders, onAction, title = "Замовлення", createPending, onCreateHandled, seedQuery, onSeedHandled }) {
  const [receipt, setReceipt] = useState(null); // {number, url}
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("active");
  const [src, setSrc] = useState("all");
  const [agentOrder, setAgentOrder] = useState(null);
  const createHandled = useRef(false);

  const openAttachment = async (o, kind) => {
    try {
      const r = await api.get(`/admin/orders/${o._id}/${kind}`, { responseType: "blob" });
      if (receipt?.url) URL.revokeObjectURL(receipt.url);
      setReceipt({ number: o.number, url: URL.createObjectURL(r.data) });
    } catch {
      toast.error(kind === "story" ? "Історія відсутня" : "Чек відсутній");
    }
  };

  const closeReceipt = () => {
    if (receipt?.url) URL.revokeObjectURL(receipt.url);
    setReceipt(null);
  };

  const createConstructor = async () => {
    try {
      const r = await api.post("/admin/constructor", {});
      setAgentOrder(r.data.order);
    } catch {
      toast.error("Не вдалося створити адмінський заказ");
    }
  };

  useEffect(() => {
    if (createPending && !createHandled.current) {
      createHandled.current = true;
      onCreateHandled?.();
      createConstructor();
    }
    if (!createPending) createHandled.current = false;
    // eslint-disable-next-line
  }, [createPending]);

  useEffect(() => {
    if (seedQuery) {
      setSearch(seedQuery);
      onSeedHandled?.();
    }
    // eslint-disable-next-line
  }, [seedQuery]);

  const digits = (s) => (s || "").replace(/\D/g, "");
  const list = orders || [];
  const ACTIVE = ["created", "screenshot_wait", "pending", "paid", "in_progress"];
  const counts = list.reduce((m, o) => { m[o.status] = (m[o.status] || 0) + 1; return m; }, {});
  const srcCounts = list.reduce((m, o) => { const k = orderSource(o); m[k] = (m[k] || 0) + 1; return m; }, {});
  const q = digits(search);
  const visible = list
    .filter((o) => (filter === "active" ? ACTIVE.includes(o.status)
      : (filter === "all" || o.status === filter)))
    .filter((o) => src === "all" || orderSource(o) === src)
    .filter((o) => !q || digits(o.number).includes(q) || digits(o.landing_contact).includes(q))
    .sort((a, b) => {
      const ra = AGENT_RANK[a.status] ?? 5;
      const rb = AGENT_RANK[b.status] ?? 5;
      if (ra !== rb) return ra - rb;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });

  const FILTERS = [
    ["active", "В роботі"], ["pending", "На перевірці"], ["in_progress", "Генеруються"],
    ["paid", "Оплачені"], ["unpaid", "Чернетки"], ["done", "Виконані"], ["all", "Всі"],
  ];
  const SRC_FILTERS = [
    ["all", "Всі джерела"], ["app", "📱 Міні-ап"], ["app_balance", "⚖️ З балансу"],
    ["topup", "💳 Поповнення"], ["site1", "🌐 Студія"], ["site2", "🌐 Конструктор"],
    ["tg", "🤖 Бот"], ["tgs", "🤖 Бот простий"], ["admin", "🎛 Адмін"], ["test", "🧪 Тест"],
  ];
  const chip = (active) => `text-xs px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap ${active
    ? "border-[#2BA8FF]/60 text-white bg-[#2BA8FF]/15"
    : "border-white/10 text-[#94A3B8] hover:text-white hover:border-white/30"}`;

  return (
    <div className="space-y-4" data-testid="admin-orders-board">
      <Dialog open={!!receipt} onOpenChange={(o) => !o && closeReceipt()}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-xl bg-[#0F1729] border-white/10 text-white p-2">
          <DialogHeader className="px-3 pt-3">
            <DialogTitle className="text-base">Чек замовлення #{receipt?.number}</DialogTitle>
          </DialogHeader>
          <img src={receipt?.url} alt="Чек" className="rounded-xl max-h-[70vh] w-full object-contain" />
        </DialogContent>
      </Dialog>

      {agentOrder && (
        <OrderAgentModal order={agentOrder} onClose={() => setAgentOrder(null)}
          onAction={onAction} onOpenAttachment={openAttachment} />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl font-extrabold text-white">{title}</h2>
          <p className="text-xs text-[#64748B] mt-0.5">
            {list.length} усього · показано {visible.length}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => onAction?.(null, "refresh")}
            className="text-xs px-3 py-2 rounded-lg bg-white/5 text-[#CBD5E1] border border-white/10 hover:border-white/30 transition-colors">
            ↻ Оновити
          </button>
          <button onClick={createConstructor}
            className="text-xs px-3 py-2 rounded-lg bg-[#EF4444]/10 text-[#FF8A8A] border border-[#EF4444]/30 hover:bg-[#EF4444]/20 transition-colors whitespace-nowrap">
            🎛 Конструктор
          </button>
        </div>
      </div>

      <input value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="🔍 Пошук: номер або телефон…"
        className="w-full px-3 py-2.5 rounded-xl bg-black/30 border border-white/10 text-sm text-white outline-none focus:border-[#2BA8FF]/60" />

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([id, name]) => {
          const n = id === "all" ? list.length
            : id === "active" ? list.filter((o) => ACTIVE.includes(o.status)).length
              : (counts[id] || 0);
          return (
            <button key={id} onClick={() => setFilter(id)} className={chip(filter === id)}>
              {name} <span className="font-mono text-[10px] opacity-70">{n}</span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {SRC_FILTERS.map(([id, name]) => (
          <button key={id} onClick={() => setSrc(id)} className={chip(src === id)}>
            {name}
            {id !== "all" && <span className="font-mono text-[10px] opacity-70"> {srcCounts[id] || 0}</span>}
          </button>
        ))}
      </div>

      {visible.length ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((o) => {
            const [cls, statusLabel] = ORDER_STATUS[o.status] || ["text-[#94A3B8]", o.status];
            const tint = STATUS_TINT[o.status] || "bg-white/[0.03] border-white/10";
            const src = orderSource(o);
            const badge = SOURCE_BADGE[src] || SOURCE_BADGE.other;
            const isTopup = src === "topup";
            const songsN = Math.max(1, Number(o.songs) || 1);
            const titleLine = isTopup
              ? `💳 Поповнення · ${songsN} ${plural(songsN, "пісня", "пісні", "пісень")}`
              : (o.landing_genre_label || o.package_name || "—");
            const subLine = isTopup
              ? "Пісні зарахуються на баланс після перевірки чека."
              : (o.landing_description || o.package_name || "—");
            const dot = STATUS_DOT[o.status] || "#94A3B8";
            const hasReceipt = !!(o.screenshot_file_id || o.landing_receipt_path);
            const canApprove = o.status === "pending" || o.status === "screenshot_wait";
            const open = () => setAgentOrder(o);
            return (
              <div key={o._id} role="button" tabIndex={0} onClick={open}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
                className={`relative overflow-hidden rounded-2xl border p-4 pl-5 text-left cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-xl hover:border-white/25 ${tint}`}>
                <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: dot }} />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>
                  <span className={`text-[10px] font-bold ${cls}`}>● {statusLabel}</span>
                  <span className={`font-mono text-xs ${o.number ? "text-[#CBD5E1]" : "text-[#64748B] italic"}`}>
                    {o.number ? `#${o.number}` : (o.status === "unpaid" ? "чернетка" : "без номера")}
                  </span>
                  <span className="ml-auto font-mono text-[10px] text-[#475569]">
                    {(o.created_at || "").slice(5, 16).replace("T", " ")}
                    {o.paid_at ? <span className="text-[#6EE7B7]"> · опл {(o.paid_at || "").slice(5, 16).replace("T", " ")}</span> : null}
                  </span>
                </div>
                <div className="mt-2.5 text-sm font-semibold text-white truncate">
                  {titleLine}
                  {o.landing_voice_label ? <span className="text-[#94A3B8] font-normal"> · {o.landing_voice_label}</span> : null}
                </div>
                <p className="review-text mt-1 text-xs text-[#94A3B8] leading-relaxed">
                  {subLine}
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] text-[#64748B] truncate max-w-[60%]">
                    {o.landing_contact || (o.user_telegram_id ? `TG ${o.user_telegram_id}` : "—")}
                  </span>
                  <span className="ml-auto text-sm font-bold text-[#EAB308]">
                    {(o.amount || 0) ? `${Math.round(o.amount)} ₴` : ""}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                  {hasReceipt && (
                    <button onClick={() => openAttachment(o, "receipt")}
                      className="text-[11px] px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-[#CBD5E1] hover:border-white/30 transition-colors">
                      🧾 Чек
                    </button>
                  )}
                  {canApprove && (
                    <>
                      <button onClick={() => onAction(o._id, "approve")}
                        className="text-[11px] px-2.5 py-1 rounded-lg bg-[#10B981]/15 border border-[#10B981]/40 text-[#6EE7B7] hover:bg-[#10B981]/25 transition-colors">
                        ✅ Зарахувати
                      </button>
                      <button onClick={() => onAction(o._id, "reject")}
                        className="text-[11px] px-2.5 py-1 rounded-lg bg-[#EF4444]/15 border border-[#EF4444]/40 text-[#FF8A8A] hover:bg-[#EF4444]/25 transition-colors">
                        ✕
                      </button>
                    </>
                  )}
                  <select value={o.status}
                    onChange={(e) => onAction(o._id, e.target.value)}
                    className={`ml-auto text-[10px] px-2 py-1 rounded-lg border border-white/15 bg-[#0F1729] cursor-pointer outline-none ${cls}`}>
                    {AGENT_STATUS_OPTIONS.map(([v, l]) => (
                      <option key={v} value={v} className="bg-[#0F1729] text-white">{l}</option>
                    ))}
                  </select>
                  <button onClick={open}
                    className="text-[11px] px-2.5 py-1 rounded-lg bg-[#2BA8FF]/15 border border-[#2BA8FF]/40 text-[#8CCBFF] hover:bg-[#2BA8FF]/25 transition-colors">
                    Відкрити →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card-soft rounded-2xl py-16 text-center text-[#64748B]">Немає замовлень за цим фільтром.</div>
      )}
    </div>
  );
}

const Field = ({ label, value, mono }) => (
  <div className="min-w-0">
    <div className="text-[10px] font-mono uppercase tracking-wider text-[#64748B]">{label}</div>
    <div className={`text-sm text-[#CBD5E1] mt-0.5 break-words ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
  </div>
);

function OrdersTable({ songs }) {
  return (
    <div className="card-soft rounded-2xl overflow-hidden" data-testid="admin-orders-table">
      <div className="overflow-x-auto no-scrollbar">
        <table className="w-full min-w-[640px]">
          <thead className="border-b border-white/10"><tr><Th>Подія</Th><Th>Жанр</Th><Th>Настрій</Th><Th>Голос</Th><Th>Статус</Th><Th>Оплата</Th><Th>Дата</Th></tr></thead>
          <tbody>
            {songs.length ? songs.map((s, i) => (
              <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                <Td>{EVENT[s.event] || s.event || "—"}</Td>
                <Td>{GENRE[s.genre] || s.genre || "—"}</Td>
                <Td className="text-[#94A3B8]">{s.mood || "—"}</Td>
                <Td className="text-[#94A3B8]">{s.voice || "—"}</Td>
                <Td className={`font-medium ${STATUS[s.status] || ""}`}>{s.status}</Td>
                <Td>{s.price_paid ? `${s.price_paid}₴` : "—"}</Td>
                <Td className="font-mono text-xs text-[#64748B]">{(s.created_at || "").slice(0, 16).replace("T", " ")}</Td>
              </tr>
            )) : <tr><td colSpan={7} className="py-12 text-center text-[#64748B]">Ще немає замовлень.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsPanel({ settings, reload }) {
  const [saving, setSaving] = useState(null);
  const [card, setCard] = useState(settings?.payment_card || "");
  const provider = settings?.music_provider || "minimax";
  const changedAt = settings?.updated_at?.music_provider;
  const sunoReady = !!settings?.suno_configured;

  const pick = async (id) => {
    if (id === provider || saving) return;
    setSaving(id);
    try {
      await api.put("/admin/settings", { music_provider: id });
      toast.success(id === "suno" ? "Провайдер музики: Suno V6" : "Провайдер музики: MiniMax Music 3");
      await reload();
    } catch {
      toast.error("Не вдалося зберегти");
    } finally {
      setSaving(null);
    }
  };

  const saveCard = async () => {
    setSaving("card");
    try {
      await api.put("/admin/settings", { payment_card: card.trim() });
      toast.success("Картку збережено");
      await reload();
    } catch {
      toast.error("Не вдалося зберегти картку");
    } finally {
      setSaving(null);
    }
  };

  const OPTIONS = [
    ["minimax", "MiniMax Music 3", "Офіційний API через fal. Стабільно, але слабкий український вокал."],
    ["suno", "Suno V6 (sunor)", "Найкращий вокал і українська. Неофіційний реселлер — тримай запас кредитів."],
  ];
  const activeLabel = OPTIONS.find(([id]) => id === provider)?.[1] || provider;
  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="admin-settings">
      <Panel title="Провайдер музики">
        {/* Явный статус: что активно сейчас */}
        <div className="rounded-xl border-2 border-emerald-400/40 bg-emerald-400/5 px-4 py-3 mb-5">
          <div className="text-xs uppercase tracking-wider text-emerald-300/80 font-mono">активно зараз</div>
          <div className="text-lg font-bold text-white mt-1 flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400" />
            {activeLabel}
          </div>
          <div className="text-xs text-[#64748B] mt-1">змінено: {fmt(changedAt)}</div>
        </div>

        <div className="space-y-3">
          {OPTIONS.map(([id, label, hint]) => {
            const active = provider === id;
            const warn = id === "suno" && !sunoReady;
            return (
              <button
                key={id}
                data-testid={`provider-${id}`}
                disabled={!!saving}
                onClick={() => pick(id)}
                className={`w-full text-left rounded-xl px-4 py-3 border-2 transition flex gap-3 items-start ${
                  active
                    ? "border-emerald-400/70 bg-emerald-400/5"
                    : "border-white/15 hover:border-white/35"
                } ${saving ? "opacity-60 cursor-wait" : ""}`}
              >
                <span className={`mt-0.5 w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 ${
                  active ? "border-emerald-400" : "border-[#475569]"
                }`}>
                  {active && <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />}
                </span>
                <span className="flex-1">
                  <span className="text-sm font-semibold text-white flex items-center gap-2">
                    {label}
                    {active && <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-400 text-black">ОБРАНО ✓</span>}
                    {saving === id && <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/10 text-white">ЗБЕРІГАЮ…</span>}
                    {warn && <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">НЕМА КЛЮЧА</span>}
                  </span>
                  <span className="block text-xs mt-1 text-[#64748B]">{hint}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="text-xs text-[#64748B] mt-4">
          Натисни на варіант, щоб перемкнути. Діє для наступної генерації — перезапуск не потрібен.
        </div>
      </Panel>
      <Panel title="Реквізити для оплати">
        <div className="text-sm text-[#94A3B8] mb-3">
          Номер картки, який бот показує клієнту на екрані оплати замовлення.
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="payment-card-input"
            value={card}
            onChange={(e) => setCard(e.target.value)}
            placeholder="0000 0000 0000 0000"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 font-mono focus:outline-none focus:border-[#F97316]"
          />
          <button
            data-testid="payment-card-save"
            disabled={!!saving}
            onClick={saveCard}
            className="grid place-items-center w-10 h-10 rounded-xl grad-cta shrink-0 disabled:opacity-50"
          >
            {saving === "card" ? <RefreshCw size={16} className="text-white animate-spin" /> : <Save size={16} className="text-white" />}
          </button>
        </div>
        {!card && <div className="text-xs text-[#EF4444] mt-2">Порожньо — бот покаже «картку не налаштовано» замість реквізитів.</div>}
      </Panel>
      <Panel title="Як це працює">
        <ul className="text-sm text-[#94A3B8] space-y-2 list-disc pl-4">
          <li><b className="text-white">MiniMax Music 3</b> — приймає наш структурований капшн (3 блоки, таймлайн по секціях) та ударения в тексті.</li>
          <li><b className="text-white">Suno V6</b> — приймає текст пісні + короткі стиль-теги; ударения не потрібні, демо ріжеться з повного треку (після оплати віддається той самий трек).</li>
          <li>Текст, вичитка й обкладинки завжди працюють через fal — тут перемикається лише музика.</li>
          <li>Якщо у вибраного провайдера стався збій — перемкни на інший, стан пісні збережеться.</li>
        </ul>
      </Panel>
    </div>
  );
}

function Packages({ packages, reload }) {
  const [edit, setEdit] = useState({});
  const save = async (key) => {
    try {
      await api.put(`/admin/packages/${key}`, { price_uah: Number(edit[key]) });
      toast.success("Ціну оновлено");
      reload();
    } catch { toast.error("Помилка збереження"); }
  };
  return (
    <div className="grid md:grid-cols-3 gap-5" data-testid="admin-packages">
      {packages.map((p) => (
        <div key={p.key} className={`card-soft rounded-2xl p-6 ${p.is_popular ? "border-[#F97316]/50" : ""}`}>
          <div className="flex items-center justify-between">
            <h3 className="font-bold">{p.name_ua}</h3>
            {p.is_popular && <span className="text-xs grad-cta text-white px-2 py-0.5 rounded-full font-mono">HIT</span>}
          </div>
          <p className="text-xs text-[#64748B] font-mono mt-1">{p.songs_count} ген. • {p.key}</p>
          <div className="flex items-center gap-2 mt-4">
            <input
              data-testid={`package-price-${p.key}`}
              type="number" defaultValue={p.price_uah}
              onChange={(e) => setEdit({ ...edit, [p.key]: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 focus:outline-none focus:border-[#F97316]"
            />
            <span className="text-[#64748B]">₴</span>
            <button data-testid={`package-save-${p.key}`} onClick={() => save(p.key)} className="grid place-items-center w-10 h-10 rounded-xl grad-cta shrink-0"><Save size={16} className="text-white" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

const LOG_LEVEL_C = { info: "text-[#38BDF8]", error: "text-[#EF4444]", warn: "text-[#EAB308]" };
const LOG_TYPE_C = {
  button: "text-[#A78BFA]", message: "text-[#38BDF8]", lyrics: "text-[#EAB308]",
  music: "text-[#F97316]", payment: "text-[#10B981]", fal_call: "text-[#EC4899]",
};

function UsagePanel({ usage }) {
  if (!usage) return <div className="text-[#64748B] text-sm">Завантаження…</div>;
  const cards = [
    { l: "Викликів ШІ", v: usage.calls ?? 0, icon: Sparkles, c: C.accent },
    { l: "Фоллбеків (на Sonnet 4.5)", v: usage.fallbacks ?? 0, icon: RefreshCw, c: C.gold },
    { l: "Витрати LLM (реальні)", v: `$${(usage.cost ?? 0).toFixed(4)}`, icon: DollarSign, c: C.green },
  ];
  const tables = [["За моделями", "Модель", usage.models], ["За етапами", "Етап", usage.stages]];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div key={c.l} className="rounded-2xl border border-white/10 p-4" style={{ background: C.card }}>
            <div className="flex items-center gap-2 text-[#94A3B8] text-xs">
              <c.icon size={14} style={{ color: c.c }} />{c.l}
            </div>
            <div className="text-2xl font-black text-white mt-2">{c.v}</div>
          </div>
        ))}
      </div>
      {tables.map(([title, first, rows]) => (
        <div key={title} className="rounded-2xl border border-white/10 overflow-hidden" style={{ background: C.card }}>
          <div className="px-4 py-3 text-sm font-bold text-white border-b border-white/10">{title}</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#64748B] text-xs text-left">
                <th className="px-4 py-2">{first}</th>
                <th className="px-4 py-2">Викликів</th>
                <th className="px-4 py-2">Фоллбеків</th>
                <th className="px-4 py-2">Токени вх/вих</th>
                <th className="px-4 py-2">$</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((r) => (
                <tr key={r.key} className="border-t border-white/5 text-[#CBD5E1]">
                  <td className="px-4 py-2 font-mono text-xs">{r.key}</td>
                  <td className="px-4 py-2">{r.calls}</td>
                  <td className="px-4 py-2">{r.fallbacks || "—"}</td>
                  <td className="px-4 py-2 text-xs">{r.tin} / {r.tout}</td>
                  <td className="px-4 py-2">${(r.cost ?? 0).toFixed(4)}</td>
                </tr>
              ))}
              {!(rows || []).length && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-[#475569]">Даних немає</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function LogsPanel() {
  const [logs, setLogs] = useState([]);
  const [level, setLevel] = useState("");
  const [auto, setAuto] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/logs", { params: { limit: 400, level: level || undefined } });
      setLogs(r.data);
    } catch { /* ignore transient */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); /* eslint-disable-next-line */ }, [level]);
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(fetchLogs, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [auto, level]);

  const clear = async () => {
    if (!window.confirm("Очистити всі логи?")) return;
    try { await api.delete("/admin/logs"); toast.success("Логи очищено"); fetchLogs(); }
    catch { toast.error("Помилка"); }
  };

  const LEVELS = [["", "Усі"], ["info", "Info"], ["error", "Помилки"]];

  return (
    <div className="card-soft rounded-2xl overflow-hidden" data-testid="admin-logs-table">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <ScrollText size={17} className="text-[#F97316]" />
          <span className="font-semibold">Системні логи</span>
          <span className="text-xs text-[#64748B] font-mono">{logs.length}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {LEVELS.map(([v, name]) => (
            <button key={v || "all"} data-testid={`admin-logs-filter-${v || "all"}`} onClick={() => setLevel(v)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${level === v ? "border-[#F97316] text-white bg-[#F97316]/10" : "border-white/10 text-[#64748B] hover:text-white"}`}>
              {name}
            </button>
          ))}
          <button data-testid="admin-logs-auto" onClick={() => setAuto((a) => !a)}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${auto ? "border-[#10B981] text-[#10B981] bg-[#10B981]/10" : "border-white/10 text-[#64748B] hover:text-white"}`}>
            Авто {auto ? "ON" : "OFF"}
          </button>
          <button data-testid="admin-logs-refresh" onClick={fetchLogs}
            className="grid place-items-center w-8 h-8 rounded-lg border border-white/10 hover:border-[#F97316]/50 transition-colors">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
          <button data-testid="admin-logs-clear" onClick={clear}
            className="grid place-items-center w-8 h-8 rounded-lg border border-white/10 hover:border-[#EF4444]/50 transition-colors text-[#EF4444]">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <div className="overflow-x-auto no-scrollbar max-h-[560px]">
        <table className="w-full min-w-[920px]">
          <thead className="border-b border-white/10 sticky top-0 bg-[#0B0F19]/95 backdrop-blur">
            <tr><Th>Час</Th><Th>Користувач</Th><Th>Тип</Th><Th>Рівень</Th><Th>Повідомлення</Th><Th>Модель / endpoint</Th></tr>
          </thead>
          <tbody>
            {logs.length ? logs.map((l, i) => (
              <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                <Td className="font-mono text-xs text-[#64748B] whitespace-nowrap">{(l.created_at || "").slice(0, 19).replace("T", " ")}</Td>
                <Td className="font-mono text-xs text-[#94A3B8] whitespace-nowrap">{l.username ? `@${l.username}` : (l.telegram_id || "—")}</Td>
                <Td className={`font-mono text-xs font-medium ${LOG_TYPE_C[l.event_type] || "text-[#94A3B8]"}`}>{l.event_type}</Td>
                <Td className={`font-mono text-xs font-medium ${LOG_LEVEL_C[l.level] || ""}`}>{l.level}</Td>
                <Td className="text-sm max-w-[360px] truncate" title={l.message}>{l.message}</Td>
                <Td className="font-mono text-xs text-[#64748B]">{l.meta?.model || l.meta?.endpoint || "—"}</Td>
              </tr>
            )) : <tr><td colSpan={6} className="py-12 text-center text-[#64748B]">Ще немає логів. Дії в боті з'являться тут автоматично.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReviewsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", date: "", text: "", lang: "uk" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/reviews");
      setItems(r.data?.items || []);
    } catch { toast.error("Не вдалося завантажити відгуки"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (busy) return;
    if (form.name.trim().length < 2) { toast.error("Вкажіть ім'я"); return; }
    if (form.text.trim().length < 10) { toast.error("Текст відгуку закороткий"); return; }
    setBusy(true);
    try {
      await api.post("/admin/reviews", {
        name: form.name.trim(), date: form.date.trim(), text: form.text.trim(),
        lang: form.lang,
      });
      toast.success("Відгук додано на сайт");
      setForm({ name: "", date: "", text: "", lang: "uk" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не вдалося додати");
    } finally { setBusy(false); }
  };

  const setReviewLang = async (it, lang) => {
    try {
      await api.patch(`/admin/reviews/${it.id}`, { lang });
      toast.success(lang === "en" ? "Мова: EN" : "Мова: UA");
      await load();
    } catch { toast.error("Не вдалося змінити мову"); }
  };

  const setStatus = async (it, status) => {
    try {
      await api.patch(`/admin/reviews/${it.id}`, { status });
      toast.success(status === "approved" ? "Опубліковано"
        : status === "rejected" ? "Відхилено" : "Оновлено");
      await load();
    } catch { toast.error("Не вдалося оновити"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Видалити відгук від «${it.name}»?`)) return;
    try {
      await api.delete(`/admin/reviews/${it.id}`);
      toast.success("Видалено");
      await load();
    } catch { toast.error("Не вдалося видалити"); }
  };

  const STATUS = {
    approved: ["на сайті", "bg-[#10B981]/15 text-[#10B981]"],
    pending: ["на модерації", "bg-[#FFD400]/15 text-[#FFD400]"],
    rejected: ["відхилено", "bg-[#EF4444]/15 text-[#EF4444]"],
  };

  return (
    <div className="space-y-5" data-testid="admin-reviews">
      <div className="card-soft rounded-2xl p-4">
        <div className="text-sm font-bold text-white mb-3">Додати відгук (з'явиться на сайті одразу)</div>
        <div className="grid sm:grid-cols-[1fr_200px_110px] gap-2">
          <input value={form.name} maxLength={40}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Ім'я (напр. Олена К.)"
            className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none focus:border-white/30" />
          <input value={form.date} maxLength={20}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            placeholder="Дата текстом (напр. 12.05.2026)"
            className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none focus:border-white/30" />
          <select value={form.lang}
            onChange={(e) => setForm((f) => ({ ...f, lang: e.target.value }))}
            className="px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none focus:border-white/30">
            <option value="uk">UA сайт</option>
            <option value="en">EN сайт</option>
          </select>
        </div>
        <textarea value={form.text} rows={3} maxLength={1000}
          onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
          placeholder="Текст відгуку"
          className="mt-2 w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white outline-none focus:border-white/30 resize-none" />
        <button onClick={create} disabled={busy}
          className="btn-tg grad-tg text-white font-bold text-sm px-5 py-2 rounded-lg mt-2 disabled:opacity-50">
          {busy ? "…" : "Додати відгук"}
        </button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="card-soft rounded-2xl p-6 text-center text-[#64748B] text-sm">Відгуків ще немає</div>
        )}
        {items.map((it) => {
          const [label, cls] = STATUS[it.status] || STATUS.pending;
          return (
            <div key={it.id} className="card-soft rounded-2xl p-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-bold text-white text-sm">{it.name}</span>
                <span className="text-xs text-[#64748B] font-mono">{it.date}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-wider ${cls}`}>{label}</span>
                <span className="text-[10px] text-[#475569] font-mono uppercase">{it.source}</span>
                <select value={it.lang || "uk"} onChange={(e) => setReviewLang(it, e.target.value)}
                  title="Мова відгуку (UA/EN)"
                  className="text-[10px] font-mono uppercase bg-white/5 border border-white/10 rounded-md px-1.5 py-0.5 text-[#94A3B8] outline-none">
                  <option value="uk">UA</option>
                  <option value="en">EN</option>
                </select>
                <span className="ml-auto flex items-center gap-2">
                  {it.status === "pending" && (
                    <>
                      <button onClick={() => setStatus(it, "approved")}
                        className="text-xs bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30 rounded-lg px-2.5 py-1 hover:bg-[#10B981]/20">✅ Опублікувати</button>
                      <button onClick={() => setStatus(it, "rejected")}
                        className="text-xs bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 rounded-lg px-2.5 py-1 hover:bg-[#EF4444]/20">❌ Відхилити</button>
                    </>
                  )}
                  {it.status === "approved" && (
                    <button onClick={() => setStatus(it, "pending")}
                      className="text-xs bg-white/5 text-[#94A3B8] border border-white/10 rounded-lg px-2.5 py-1 hover:bg-white/10">Зняти з сайту</button>
                  )}
                  <button onClick={() => remove(it)} title="Видалити"
                    className="text-[#EF4444] hover:text-[#F87171] px-1"><Trash2 size={14} /></button>
                </span>
              </div>
              <p className="mt-2 text-sm text-[#CBD5E1] leading-relaxed whitespace-pre-wrap">{it.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
